import React, { useState, useEffect, useCallback, useRef } from "react";
import { Chess } from "chess.js";
import { useStockfishPersonalised } from "./StockfishPersonalised";
import { useNavigate, useLocation } from "react-router-dom";
import { Chessboard } from "react-chessboard";
import {
  formatDbPuzzleToStateStructure,
  puzzleStatusMessages,
} from "../../Utils/HelperPersonalisedPuzzles";
import { stockfishSocketService } from "./StockfishSocketService";
import axios from "../../Utils/axiosConfig";
import { ReactComponent as FlipIcon } from "../Assets/Flip-Board.svg";
import "../Signup/Signup.css";

const EVAL_DIFF_THRESHOLD = 150;
const EVAL_BUFFER = 30;
const SCAN_DEPTH = 20;

const calculateMaxPliesForDisplay = (
  variation,
  blunderDetails,
  EVAL_DIFF_THRESHOLD
) => {
  if (!variation || variation.length === 0) return 0;

  const reportedMateIn = variation[0]?.mateIn;
  const absMateIn = reportedMateIn !== null ? Math.abs(reportedMateIn) : null;

  const prevEvalCp = blunderDetails.prevEvalCp;
  const evalDropCp = blunderDetails.evalDropCp;
  const userColor = blunderDetails.userColor;
  const normalizedPrevEvalCp = userColor === "w" ? prevEvalCp : -prevEvalCp;

  if (absMateIn !== null && absMateIn >= 1 && absMateIn <= 4) {
    return variation.length;
  } else if (absMateIn !== null && absMateIn >= 5 && absMateIn <= 7) {
    return Math.min(variation.length, 5);
  } else if (absMateIn !== null && absMateIn >= 8) {
    return Math.min(variation.length, 5);
  } else {
    if (evalDropCp >= 500) {
      return Math.min(variation.length, 1);
    } else if (
      normalizedPrevEvalCp >= 600 &&
      evalDropCp >= EVAL_DIFF_THRESHOLD
    ) {
      return Math.min(variation.length, 1);
    } else if (
      Math.abs(normalizedPrevEvalCp) <= 250 &&
      evalDropCp >= 150 &&
      evalDropCp <= 250
    ) {
      return Math.min(variation.length, 5);
    } else if (
      Math.abs(normalizedPrevEvalCp) <= 250 &&
      evalDropCp > 250 &&
      evalDropCp <= 500
    ) {
      return Math.min(variation.length, 3);
    } else if (
      ((normalizedPrevEvalCp >= -600 && normalizedPrevEvalCp <= -250) ||
        (normalizedPrevEvalCp >= 250 && normalizedPrevEvalCp < 600)) &&
      evalDropCp >= 150 &&
      evalDropCp <= 250
    ) {
      return Math.min(variation.length, 3);
    } else if (
      ((normalizedPrevEvalCp >= -600 && normalizedPrevEvalCp <= -250) ||
        (normalizedPrevEvalCp >= 250 && normalizedPrevEvalCp <= 600)) &&
      evalDropCp > 250 &&
      evalDropCp <= 500
    ) {
      return Math.min(variation.length, 1);
    } else {
      return Math.min(variation.length, 5);
    }
  }
};

let isAudioUnlocked = false;
const soundQueue = [];

const playSoundInternal = (soundUrl, volume) => {
  try {
    const audio = new Audio(soundUrl);
    audio.volume = volume;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.warn(
          `Could not play sound: ${soundUrl}. This might be due to browser policy or a file path error.`,
          error.name
        );
      });
    }
  } catch (e) {
    console.error(`Error creating audio object for: ${soundUrl}`, e);
  }
};

const unlockAudio = () => {
  if (isAudioUnlocked) return;

  const silentAudio = new Audio(
    "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA"
  );
  silentAudio.play().catch(() => {});

  isAudioUnlocked = true;
  soundQueue.forEach((sound) => playSoundInternal(sound.url, sound.volume));
  soundQueue.length = 0;
};

const playSound = (soundUrl, volume = 1.0) => {
  if (isAudioUnlocked) {
    playSoundInternal(soundUrl, volume);
  } else {
    soundQueue.push({ url: soundUrl, volume: volume });
  }
};

const playSoundForMove = (move) => {
  if (!move) return;

  if (move.san.includes("#")) {
    playSound("/sounds/checkmate.mp3");
  } else if (move.flags.includes("p")) {
    playSound("/sounds/promote.mp3");
  } else if (move.flags.includes("k") || move.flags.includes("q")) {
    playSound("/sounds/castle.mp3");
  } else if (move.san.includes("+")) {
    playSound("/sounds/move-check.mp3");
  } else if (move.flags.includes("c") || move.flags.includes("e")) {
    playSound("/sounds/capture.mp3");
  } else {
    playSound("/sounds/move-self.mp3");
  }
};

const playPuzzleSuccessSound = () => playSound("/sounds/correct.mp3", 0.1);
const playPuzzleFailSound = () => playSound("/sounds/incorrect.mp3", 1.0);
const playIllegalMoveSound = () => playSound("/sounds/illegal.mp3");

export const PersonalisedPuzzles = () => {
  const [games, setGames] = useState([]);
  const [puzzleRating, setPuzzleRating] = useState(null);
  const [personalisedRatingChange, SetPersonalisedRatingChange] =
    useState(null);
  const [loadingRating, setLoadingRating] = useState(true);
  const [loadingGames, setLoadingGames] = useState(true);
  const [loadingPuzzles, setLoadingPuzzles] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState(null);
  const [chesscomUsername, setChesscomUsername] = useState(null);
  const [lichessUsername, setLichessUsername] = useState(null);
  const [puzzleStates, setPuzzleStates] = useState([]);
  const [existingPuzzlesMap, setExistingPuzzlesMap] = useState(new Map());
  const [processedGameIds, setProcessedGameIds] = useState(new Set());
  const [loadingProcessedGames, setLoadingProcessedGames] = useState(true);
  const [currentPuzzleIndex, setCurrentPuzzleIndex] = useState(0);
  const [showLiveAnalysisPanel, setShowLiveAnalysisPanel] = useState(false);
  const [customSquareStyles, setCustomSquareStyles] = useState({});
  const [moveFrom, setMoveFrom] = useState("");
  const [optionSquares, setOptionSquares] = useState({});
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  const [promotionSquare, setPromotionSquare] = useState(null);
  const [promotionMove, setPromotionMove] = useState(null);
  const [hintsUsedPerMove, setHintsUsedPerMove] = useState([]);
  const [currentMoveHintLevel, setCurrentMoveHintLevel] = useState(0);
  const [hintMove, setHintMove] = useState(null);
  const [isHintLoading, setIsHintLoading] = useState(false);
  const [isContinuePlayingActive, setIsContinuePlayingActive] = useState(false);
  const [puzzleHasBeenSolvedOnce, setPuzzleHasBeenSolvedOnce] = useState(false);
  const [isShowingMoreMovesActive, setIsShowingMoreMovesActive] =
    useState(false);
  const [puzzleHasBeenFailedOnce, setPuzzleHasBeenFailedOnce] = useState(false);
  const [hasBeenReset, setHasBeenReset] = useState(false);
  const [hasInitiatedContinuePlaying, setHasInitiatedContinuePlaying] =
    useState(false);
  const [hasActivatedLiveAnalysis, setHasActivatedLiveAnalysis] =
    useState(false);
  const [hasInitiatedSelfAnalysis, setHasInitiatedSelfAnalysis] =
    useState(false);
  const [isShowingBlunderAnalysis, setIsShowingBlunderAnalysis] =
    useState(false);
  const [hasShownCounter, setHasShownCounter] = useState(false);

  const [bestMoveArrow, setBestMoveArrow] = useState(null);
  const [lastMoveDisplay, setLastMoveDisplay] = useState(null);
  const [boardOrientation, setBoardOrientation] = useState("white");
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [pgnInputText, setPgnInputText] = useState("");
  const [importError, setImportError] = useState(null);

  const moveDisplayTimeoutRef = useRef(null);
  const isShowingMoreMovesActiveRef = useRef(isShowingMoreMovesActive);
  const continuePlayingStartMoveIndex = useRef(0);
  const analysisTimeoutRef = useRef(null);
  const lastAnalysisFenRef = useRef("");
  const analysisActiveRef = useRef(false);
  const evalCacheRef = useRef(new Map());
  const ratingUpdatedForPuzzleIdRef = useRef(null);
  const personalisedRatingChangeTimeoutRef = useRef(null);
  const isShowingBlunderAnalysisRef = useRef(isShowingBlunderAnalysis);
  const currentPuzzleIndexRef = useRef(currentPuzzleIndex);
  const pendingGameIdsRef = useRef(new Set());

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isArchiveMode = searchParams.get("mode") === "archive";

  const handleNewPuzzleFromBackend = useCallback((newPuzzle) => {
    setPuzzleStates((prevPuzzles) => {
      const puzzleExists = prevPuzzles.some((p) => p.id === newPuzzle.id);
      if (puzzleExists) {
        return prevPuzzles;
      }
      console.log(
        `[Puzzles] Appending new puzzle: ${newPuzzle.id} | Total: ${
          prevPuzzles.length + 1
        }`
      );
      return [...prevPuzzles, newPuzzle];
    });
  }, []);

  const handlePuzzleGenerationComplete = useCallback(() => {
    console.log("Frontend (Stage 3): Puzzle generation completed by backend.");
  }, []);

  const {
    isSocketConnected,
    startAnalysis,
    stopAnalysis,
    isAnalyzing: isLiveAnalyzing,
    analysisData,
    isGeneratingPuzzles,
    analysisRun,
    sendPuzzleGenerationCommand,
    getOneShotEvaluation,
  } = useStockfishPersonalised({
    onNewPuzzle: handleNewPuzzleFromBackend,
    onPuzzleGenerationComplete: handlePuzzleGenerationComplete,
  });
  const isGeneratingPuzzlesRef = useRef(isGeneratingPuzzles);

  const customLightSquareStyle = { backgroundColor: "#AE8FD6" };
  const customDarkSquareStyle = { backgroundColor: "#4F3B7B" };
  const currentPuzzle = puzzleStates[currentPuzzleIndex];

  const cleanupMoveState = () => {
    setMoveFrom("");
    setOptionSquares({});
    setCurrentMoveHintLevel(0);
    setHintMove(null);
    setBestMoveArrow(null);
  };

  const handleBackToQueue = useCallback(() => {
    setLoadingPuzzles(true);
    navigate("?puzzle=1");
  }, [navigate]);

  const handleOpenArchive = useCallback(() => {
    navigate("?puzzle=1&mode=archive");
  }, [navigate]);

  const handleBoardFlip = useCallback(() => {
    setBoardOrientation((prev) => (prev === "white" ? "black" : "white"));
    setCustomSquareStyles({});
  }, []);

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
    setPgnInputText("");
    setImportError(null);
  };

  const applyComputerMove = useCallback(
    (moveSan, puzzle, index, fenAfterUserMove, updatePuzzleStates, source) => {
      console.log(
        `%c[applyComputerMove] Applying move: ${moveSan} (Source: ${source})`,
        "color: blue; font-weight: bold;"
      );
      setTimeout(() => {
        updatePuzzleStates((prevStates) => {
          const newStates = [...prevStates];
          if (!newStates[index]) return prevStates;
          let puzzleToUpdate = { ...newStates[index] };

          if (puzzleToUpdate.currentFen !== fenAfterUserMove) {
            console.log(
              "[applyComputerMove - Delayed] Skipping apply: State mismatch."
            );
            return prevStates;
          }

          const gameForComputerMove = new Chess(puzzleToUpdate.currentFen);
          let computerMoveResult;
          try {
            computerMoveResult = gameForComputerMove.move(moveSan);
            if (computerMoveResult) playSoundForMove(computerMoveResult);
          } catch (e) {
            console.error(
              `[applyComputerMove - Delayed] Error making computer move ${moveSan}:`,
              e
            );
            puzzleToUpdate.blunderDetails.incorrectMove = true;
            puzzleToUpdate.isPuzzleInProgress = false;
            puzzleToUpdate.awaitingDynamicEngineMove = false;
            newStates[index] = puzzleToUpdate;
            return newStates;
          }

          if (computerMoveResult) {
            puzzleToUpdate.currentFen = gameForComputerMove.fen();
            puzzleToUpdate.moveHistory.push(gameForComputerMove.fen());
            puzzleToUpdate.currentMoveIndex++;
            puzzleToUpdate.awaitingDynamicEngineMove = false;

            puzzleToUpdate.activeVariationPlyIndex += 1;
            puzzleToUpdate.isPuzzleInProgress = true;

            console.log(
              `[applyComputerMove] abcd Ply index updated to: ${puzzleToUpdate.activeVariationPlyIndex}`
            );
          } else {
            console.warn(
              `[applyComputerMove - Delayed] computerMoveResult was null for ${moveSan}.`
            );
            puzzleToUpdate.blunderDetails.incorrectMove = true;
            puzzleToUpdate.isPuzzleInProgress = false;
            puzzleToUpdate.awaitingDynamicEngineMove = false;
          }

          newStates[index] = puzzleToUpdate;
          return newStates;
        });
      }, 800);
    },
    []
  );

  const formatBestMoveSan = useCallback((bestMoveUci, currentFen) => {
    if (!bestMoveUci || !currentFen) return "N/A";

    try {
      const tempGame = new Chess(currentFen);
      const move = tempGame.move({
        from: bestMoveUci.slice(0, 2),
        to: bestMoveUci.slice(2, 4),
        promotion: bestMoveUci.length > 4 ? bestMoveUci[4] : undefined,
      });

      return move ? move.san : "N/A";
    } catch (e) {
      return "N/A";
    }
  }, []);

  const formatPvSan = useCallback((pvUciString, currentFen) => {
    if (!pvUciString || !currentFen) return "N/A";

    const game = new Chess(currentFen);
    const uciMoves = pvUciString.split(" ");
    const sanMoves = [];

    for (const uciMove of uciMoves) {
      try {
        const move = game.move({
          from: uciMove.slice(0, 2),
          to: uciMove.slice(2, 4),
          promotion: uciMove.length > 4 ? uciMove[4] : undefined,
        });

        if (move) {
          sanMoves.push(move.san);
        } else {
          break;
        }
      } catch (e) {
        break;
      }
    }

    return sanMoves.length > 0 ? sanMoves.join(" ") : "N/A";
  }, []);

  const resetPuzzleInteractiveState = useCallback((puzzle) => {
    if (!puzzle) return null;

    const initialFen = puzzle.initialFen;

    return {
      ...puzzle,
      currentFen: initialFen,
      currentMoveIndex: 0,
      moveHistory: [initialFen],
      playedMovesSan: [],
      fullMoveSANs: [],
      analysisMoveTree: null,
      analysisCurrentNode: null,
      puzzleInteractiveStartIndex: 0,
      activeVariationPath: null,
      activeVariationPlyIndex: -1,
      targetPlyCountForSolve: 0,
      awaitingDynamicEngineMove: false,
      isRepeatedBlunder: false,
      continuePlayFailure: false,
      blunderDetails: {
        ...puzzle.blunderDetails,
        puzzleSolved: false,
        incorrectMove: false,
        lastMove: null,
      },
    };
  }, []);

  useEffect(() => {
    isShowingBlunderAnalysisRef.current = isShowingBlunderAnalysis;
  }, [isShowingBlunderAnalysis]);

  useEffect(() => {
    currentPuzzleIndexRef.current = currentPuzzleIndex;
  }, [currentPuzzleIndex]);

  useEffect(() => {
    isGeneratingPuzzlesRef.current = isGeneratingPuzzles;
  }, [isGeneratingPuzzles]);

  useEffect(() => {
    return () => {
      console.log("[Lifecycle] PersonalisedPuzzles unmounting.");

      if (analysisTimeoutRef.current) clearTimeout(analysisTimeoutRef.current);
      if (moveDisplayTimeoutRef.current)
        clearTimeout(moveDisplayTimeoutRef.current);
      if (personalisedRatingChangeTimeoutRef.current)
        clearTimeout(personalisedRatingChangeTimeoutRef.current);

      if (!isGeneratingPuzzlesRef.current) {
        console.log("[Lifecycle] Stopping engine (Live Analysis).");
        if (stopAnalysis) stopAnalysis();
      } else {
        console.log(
          "[Lifecycle] Skipping stop engine (Puzzle Generation active in background)."
        );
      }
    };
  }, [stopAnalysis]);

  useEffect(() => {
    if (isSocketConnected && stopAnalysis) {
      const t = setTimeout(() => {
        console.log("[Lifecycle] Component mounted. flushing engine state.");
        stopAnalysis();
      }, 500);
      return () => clearTimeout(t);
    }
  }, [isSocketConnected, stopAnalysis]);

  useEffect(() => {
    const handleFirstInteraction = () => {
      unlockAudio();
    };
    window.addEventListener("mousedown", handleFirstInteraction, {
      once: true,
    });
    window.addEventListener("keydown", handleFirstInteraction, { once: true });
    window.addEventListener("touchstart", handleFirstInteraction, {
      once: true,
    });

    return () => {
      window.removeEventListener("mousedown", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
      window.removeEventListener("touchstart", handleFirstInteraction);
    };
  }, []);
  useEffect(() => {
    if (
      currentPuzzle?.blunderDetails.puzzleSolved &&
      currentPuzzle.blunderDetails.lastMove
    ) {
      const { from, to } = currentPuzzle.blunderDetails.lastMove;
      const highlightStyle = { backgroundColor: "rgba(5, 197, 31, 0.6)" };
      setCustomSquareStyles({
        [from]: highlightStyle,
        [to]: highlightStyle,
      });
    } else if (
      (currentPuzzle?.blunderDetails.incorrectMove ||
        currentPuzzle?.continuePlayFailure) &&
      currentPuzzle.blunderDetails.lastMove
    ) {
      const { from, to } = currentPuzzle.blunderDetails.lastMove;
      const highlightStyle = { backgroundColor: "rgba(231, 76, 60, 0.7)" };
      setCustomSquareStyles({
        [from]: highlightStyle,
        [to]: highlightStyle,
      });
    } else {
      setCustomSquareStyles({});
    }
  }, [
    currentPuzzle?.blunderDetails.puzzleSolved,
    currentPuzzle?.blunderDetails.incorrectMove,
    currentPuzzle?.blunderDetails.lastMove,
    currentPuzzle?.continuePlayFailure,
    currentPuzzleIndex,
  ]);

  useEffect(() => {
    console.log(
      "[Effect Username] Running effect to get username from storage."
    );

    const storedUsername =
      sessionStorage.getItem("username") || localStorage.getItem("username");

    if (!storedUsername) {
      console.log("No username found in session or local storage.");
      setError("No user session found");
      setLoadingGames(false);
      setLoadingPuzzles(false);
      return;
    }
    console.log(` Found username: ${storedUsername}`);
    setUsername(storedUsername);
  }, []);

  useEffect(() => {
    console.log(`[Effect User Accounts] Running. username state: ${username}`);
    if (!username) {
      console.log(" No username set, skipping fetch external usernames.");
      return;
    }
    console.log(` Username ${username} found, fetching linked accounts...`);
    const fetchExternalUsernames = async () => {
      setError(null);
      try {
        const response = await axios.get(`/external-accounts/${username}`);
        const data = response.data;
        console.log(
          `[Effect User Accounts] Successfully fetched linked accounts: Chess.com: ${
            data.chesscomUsername || "N/A"
          }, Lichess: ${data.lichessUsername || "N/A"}`
        );
        setChesscomUsername(data.chesscomUsername || null);
        setLichessUsername(data.lichessUsername || null);
      } catch (err) {
        console.error("[Effect User Accounts] Network or server error:", err);
        setError(err.message);
        setChesscomUsername(null);
        setLichessUsername(null);
        setLoadingGames(false);
      }
    };
    fetchExternalUsernames();
  }, [username]);

  useEffect(() => {
    if (analysisData?.evaluation && currentPuzzle?.currentFen) {
      evalCacheRef.current.set(
        currentPuzzle.currentFen,
        analysisData.evaluation
      );
    }
  }, [analysisData, currentPuzzle?.currentFen]);
  useEffect(() => {
    isShowingMoreMovesActiveRef.current = isShowingMoreMovesActive;
  }, [isShowingMoreMovesActive]);
  useEffect(() => {
    if (!username) {
      setLoadingRating(false);
      return;
    }
    const fetchUserProfileData = async () => {
      setLoadingRating(true);
      setError(null);
      try {
        const response = await axios.get(`/user-profile/${username}`);
        const data = response.data;
        setChesscomUsername(data.chesscomUsername || null);
        if (typeof data.personalizedPuzzleRating === "number") {
          setPuzzleRating(data.personalizedPuzzleRating);
        } else {
          setPuzzleRating(500);
        }
      } catch (err) {
        setError(err.message);
        console.error("error:", err);
        setChesscomUsername(null);
        setPuzzleRating(null);
      } finally {
        setLoadingRating(false);
      }
    };
    fetchUserProfileData();
  }, [username]);

  useEffect(() => {
    console.log(
      `[Effect Fetch Games & Puzzles] Running. chesscomUsername: ${chesscomUsername}, lichessUsername: ${lichessUsername}, username: ${username}`
    );

    if ((!chesscomUsername && !lichessUsername) || !username) {
      if (
        !loadingGames &&
        !chesscomUsername &&
        !lichessUsername &&
        username &&
        !error
      ) {
        console.log(
          "No Chess.com or Lichess username linked, cannot fetch games."
        );
      } else {
        console.log(
          "Missing username or linked account, skipping game/puzzle fetch."
        );
      }
      setLoadingGames(false);
      setLoadingPuzzles(false);
      return;
    }
    setLoadingGames(true);
    setLoadingPuzzles(true);
    setError(null);
    setGames([]);
    setPuzzleStates([]);
    setCurrentPuzzleIndex(0);
    setExistingPuzzlesMap(new Map());

    const fetchExistingPuzzles = async () => {
      const modeParam = isArchiveMode ? "&mode=archive" : "";
      const limit = isArchiveMode ? 50 : 20;

      console.log(`[Fetch] Loading puzzles. Archive Mode: ${isArchiveMode}`);

      try {
        const response = await axios.get(
          `/get-personalized-puzzles/${username}?limit=${limit}${modeParam}`
        );

        const dbPuzzles = response.data;

        console.log(
          `[Effect Fetch Games & Puzzles] Received ${dbPuzzles.length} puzzles from DB queue.`
        );

        const formattedPuzzles = dbPuzzles.map((p) =>
          formatDbPuzzleToStateStructure(p)
        );
        setPuzzleStates(formattedPuzzles);

        if (formattedPuzzles.length > 0) {
          if (isArchiveMode) {
            const urlPuzzleIndex = searchParams.get("puzzle");
            const targetIndex = urlPuzzleIndex
              ? parseInt(urlPuzzleIndex, 10) - 1
              : 0;

            if (targetIndex >= 0 && targetIndex < formattedPuzzles.length) {
              setCurrentPuzzleIndex(targetIndex);
            } else {
              setCurrentPuzzleIndex(0);
              navigate(`?puzzle=1${modeParam}`, { replace: true });
            }
          } else {
            setCurrentPuzzleIndex(0);
            navigate(`?puzzle=1`, { replace: true });
          }
        }

        const map = new Map();

        formattedPuzzles.forEach((puzzle) => {
          const gameId = puzzle.blunderDetails.gameId;
          if (!gameId) return;
          if (!map.has(gameId)) map.set(gameId, []);
          map.get(gameId).push(puzzle);
        });
        setExistingPuzzlesMap(map);
      } catch (err) {
        console.error(
          "[Effect Fetch Games & Puzzles] Fetch existing puzzles error:",
          err
        );
        setExistingPuzzlesMap(new Map());
      } finally {
        setLoadingPuzzles(false);
        console.log(
          "[Effect Fetch Games & Puzzles] Finished fetching existing puzzles."
        );
      }
    };

    const fetchGames = async () => {
      console.log(
        `[Effect Fetch Games & Puzzles] Fetching recent games for user: ${username} (Chess.com: ${
          chesscomUsername || "N/A"
        }, Lichess: ${lichessUsername || "N/A"})`
      );
      try {
        const response = await axios.get(`/fetch-recent-games/${username}`);
        const data = response.data;

        console.log(
          `[Effect Fetch Games & Puzzles] Received ${data.length} games from API.`
        );
        const gamesWithPgn = data.filter((game) => game.pgn);
        console.log(
          `[Effect Fetch Games & Puzzles] Filtered down to ${gamesWithPgn.length} games with PGN.`
        );
        if (gamesWithPgn.length !== data.length) {
          console.warn(
            "[Effect Fetch Games & Puzzles] Some fetched games were missing PGN data."
          );
        }
        if (gamesWithPgn.length === 0 && data.length > 0) {
          setError("Fetched games have no PGN data available for analysis.");
        }
        setGames(gamesWithPgn);
      } catch (err) {
        console.error("[Effect Fetch Games & Puzzles] Fetch games error:", err);
        const errorMsg =
          err.response?.data?.message ||
          err.message ||
          "Failed to connect to server";
        setError(errorMsg);
        setGames([]);
      } finally {
        setLoadingGames(false);
        console.log(
          "[Effect Fetch Games & Puzzles] Finished fetching recent games."
        );
      }
    };

    const fetchProcessedGameIds = async () => {
      console.log(
        `[Fetch All] Fetching processed game IDs for user: ${username}`
      );
      setLoadingProcessedGames(true);
      try {
        const response = await axios.get(`/get-processed-games/${username}`);
        const idsArray = response.data;

        setProcessedGameIds(new Set(idsArray));
        console.log(
          `[Fetch All] Fetched ${idsArray.length} processed game IDs.`
        );
      } catch (err) {
        console.error("[Fetch All] Fetch processed game IDs error:", err);
        setProcessedGameIds(new Set());
      } finally {
        setLoadingProcessedGames(false);
      }
    };
    fetchExistingPuzzles();
    fetchGames();
    fetchProcessedGameIds();
  }, [chesscomUsername, lichessUsername, username, isArchiveMode]);

  useEffect(() => {
    if (puzzleStates.length === 0) return;

    const params = new URLSearchParams(location.search);

    const puzzleQuery = params.get("puzzle");
    const queryIndex = puzzleQuery ? parseInt(puzzleQuery, 10) - 1 : -1;

    if (
      queryIndex >= 0 &&
      queryIndex < puzzleStates.length &&
      queryIndex !== currentPuzzleIndex
    ) {
      console.log(
        `[Navigation] URL changed. Setting puzzle index to: ${queryIndex}`
      );
      setLastMoveDisplay(null);
      if (moveDisplayTimeoutRef.current) {
        clearTimeout(moveDisplayTimeoutRef.current);
      }
      setCurrentPuzzleIndex(queryIndex);
    } else if (!puzzleQuery) {
      console.log(
        "[URL Sync] Initial load detected. Setting URL to ?puzzle=1."
      );
      navigate(`?puzzle=1`, { replace: true });
    }
  }, [location.search, puzzleStates.length, currentPuzzleIndex, navigate]);

  useEffect(() => {
    if (!currentPuzzle) return;

    console.log(`[Puzzle Loader] Preparing new puzzle: ${currentPuzzle.id}`);

    setHasInitiatedContinuePlaying(false);
    setHasBeenReset(false);
    setPuzzleHasBeenFailedOnce(false);
    setPuzzleHasBeenSolvedOnce(false);
    setIsShowingMoreMovesActive(false);
    setIsContinuePlayingActive(false);
    setHasActivatedLiveAnalysis(false);
    setHasInitiatedSelfAnalysis(false);
    setHasShownCounter(false);
    setBoardOrientation(
      currentPuzzle.blunderDetails.userColor === "w" ? "white" : "black"
    );
    setHintsUsedPerMove([]);
    cleanupMoveState();
    if (personalisedRatingChangeTimeoutRef.current) {
      clearTimeout(personalisedRatingChangeTimeoutRef.current);
    }
    SetPersonalisedRatingChange(null);
    setShowLiveAnalysisPanel(false);

    setBoardOrientation(
      currentPuzzle.blunderDetails.userColor === "w" ? "white" : "black"
    );
    setPuzzleStates((prevStates) => {
      const newStates = [...prevStates];
      const puzzleToUpdate = resetPuzzleInteractiveState(
        newStates[currentPuzzleIndex]
      );
      if (puzzleToUpdate) {
        newStates[currentPuzzleIndex] = puzzleToUpdate;
      }
      return newStates;
    });
  }, [currentPuzzleIndex, resetPuzzleInteractiveState, currentPuzzle?.id]);

  useEffect(() => {
    if (
      !currentPuzzle ||
      !currentPuzzle.blunderDetails.opponentLastMoveSanBeforeBlunder
    ) {
      return;
    }

    if (currentPuzzle.moveHistory.length > 1) {
      return;
    }

    const opponentMoveSan =
      currentPuzzle.blunderDetails.opponentLastMoveSanBeforeBlunder;

    const moveTimeout = setTimeout(() => {
      setPuzzleStates((prevStates) => {
        const newStates = [...prevStates];
        if (
          !newStates[currentPuzzleIndex] ||
          newStates[currentPuzzleIndex].id !== currentPuzzle.id
        ) {
          return prevStates;
        }

        const puzzleToUpdate = { ...newStates[currentPuzzleIndex] };
        const game = new Chess(puzzleToUpdate.initialFen);
        const moveResult = game.move(opponentMoveSan);

        if (moveResult) {
          playSoundForMove(moveResult);

          puzzleToUpdate.currentFen = game.fen();
          puzzleToUpdate.moveHistory = [puzzleToUpdate.initialFen, game.fen()];
          puzzleToUpdate.currentMoveIndex = 1;
          puzzleToUpdate.puzzleInteractiveStartIndex = 1;

          newStates[currentPuzzleIndex] = puzzleToUpdate;
        } else {
          console.error(
            `[Live Move] Failed to make opponent's move: ${opponentMoveSan}`
          );
        }

        return newStates;
      });
    }, 800);

    return () => clearTimeout(moveTimeout);
  }, [currentPuzzle]);

  useEffect(() => {
    if (loadingGames || loadingPuzzles || loadingProcessedGames) {
      console.log(
        " [Effect Analyze] Skipping: Games or existing puzzles still loading."
      );
      return;
    }
    if (isGeneratingPuzzles) {
      console.log(
        " [Effect Analyze] Skipping: Puzzle generation is already in progress according to the service."
      );
      return;
    }
    if (!chesscomUsername && !lichessUsername) {
      console.log(
        " [Effect Analyze] Skipping: No Chess.com or Lichess username available."
      );
      return;
    }

    if (analysisRun) {
      console.log(
        " [Effect Analyze] Skipping: Analysis has already been run for this session."
      );
      return;
    }

    if (stockfishSocketService.isGeneratingPuzzles) {
      console.log(
        " [Effect Analyze] Skipping: Service reports generation is active."
      );
      return;
    }

    const initialPuzzles = [];
    existingPuzzlesMap.forEach((puzzlesList) => {
      initialPuzzles.push(...puzzlesList);
    });

    if (initialPuzzles.length > 0) {
      setPuzzleStates((prev) => {
        if (prev.length > 0) {
          return prev;
        }
        console.log(
          `[Effect Analyze] Pre-loading ${initialPuzzles.length} puzzles from the database.`
        );
        return initialPuzzles;
      });
    }

    const processNewGames = () => {
      const gamesToAnalyze = games.filter((game) => {
        const rawGameId = game.url?.split("/").pop();
        const compositeGameId =
          game.source && rawGameId ? `${game.source}-${rawGameId}` : rawGameId;
        return (
          compositeGameId &&
          !existingPuzzlesMap.has(compositeGameId) &&
          !processedGameIds.has(compositeGameId)
        );
      });

      if (gamesToAnalyze.length > 0) {
        console.log(
          `[Effect Analyze] Found ${gamesToAnalyze.length} new games to analyze. Sending to backend.`
        );

        gamesToAnalyze.forEach((game) => {
          const rawGameId = game.url?.split("/").pop();
          const compositeGameId =
            game.source && rawGameId
              ? `${game.source}-${rawGameId}`
              : rawGameId;
          pendingGameIdsRef.current.add(compositeGameId);
        });

        sendPuzzleGenerationCommand(gamesToAnalyze, {
          username,
          chesscomUsername,
          lichessUsername,
        });
      } else {
        console.log(
          "[Effect Analyze] No new games to analyze. Analysis cycle complete."
        );
      }
    };

    if (games.length > 0 && isSocketConnected) {
      processNewGames();
    } else if (!loadingGames && !loadingPuzzles && games.length === 0) {
      console.log("[Effect Analyze] No games found to process.");
    }
  }, [
    games,
    existingPuzzlesMap,
    processedGameIds,
    loadingGames,
    loadingPuzzles,
    loadingProcessedGames,
    isSocketConnected,
    isGeneratingPuzzles,
    analysisRun,
    username,
    chesscomUsername,
    lichessUsername,
    sendPuzzleGenerationCommand,
  ]);

  const savePuzzlesToDatabase = async (newPuzzlesData) => {
    if (!username) {
      console.error("Cannot save: internal username missing.");
      return;
    }
    if (!chesscomUsername && !lichessUsername) {
      console.error("Cannot save: No Chess.com or Lichess username linked.");
      return;
    }
    if (newPuzzlesData.length === 0) {
      console.log("No new puzzles to save.");
      return;
    }

    const puzzlesToSaveForDb = newPuzzlesData.map((p) => ({
      username: p.userId,
      chesscomUsername: p.chesscomUsername,
      lichessUsername: p.lichessUsername,
      gameId: p.gameId,
      gameUrl: p.gameUrl,
      gameSource: p.gameSource,
      puzzleFen: p.puzzleFen,
      opponentUsername: p.opponentUsername,
      opponentLastMoveSanBeforeBlunder: p.opponentLastMoveSanBeforeBlunder,
      userPlayedAs: p.userPlayedAs,
      blunderMoveSan: p.blunderMoveSan,
      blunderPly: p.blunderPly,
      evaluationBeforeCp: p.evaluationBeforeCp,
      evaluationAfterCp: p.evaluationAfterCp,
      evaluationDropCp: p.evaluationDropCp,
      fenBeforeOpponent: p.fenBeforeOpponent,
      topVariations: p.topVariations,
      isFullyAnalyzed: p.isFullyAnalyzed,
    }));

    try {
      const response = await axios.post(
        "/save-personalized-puzzles",
        puzzlesToSaveForDb
      );
      const result = response.data;
      console.log("Puzzles saved successfully to DB:", result.message);
    } catch (err) {
      console.error("Network/fetch error saving puzzles to DB:", err);
      throw err;
    }
  };

  const markGamesAsProcessed = async (gameIdsToMark) => {
    if (!username) {
      console.error(
        "Cannot mark games as processed: internal username is not set."
      );
      return;
    }
    if (gameIdsToMark.length === 0) {
      console.log("No game IDs provided to mark as processed.");
      return;
    }

    console.log(
      `Sending ${gameIdsToMark.length} game IDs to mark as processed.`
    );
    try {
      const response = await axios.post("/mark-games-processed", {
        username,
        gameIds: gameIdsToMark,
      });

      const result = response.data;
      console.log("Games marked as processed successfully:", result.message);
    } catch (err) {
      console.error("Network/fetch error marking games as processed:", err);
      throw err;
    }
  };

  const handleShowBlunderAnalysis = useCallback(async () => {
    if (
      !currentPuzzle ||
      !currentPuzzle.blunderDetails.incorrectMove ||
      isShowingBlunderAnalysisRef.current
    ) {
      return;
    }

    setLastMoveDisplay(null);
    setBestMoveArrow(null);
    setIsShowingBlunderAnalysis(true);
    setPuzzleHasBeenFailedOnce(true);
    setHasShownCounter(true);
    SetPersonalisedRatingChange(null);
    setCustomSquareStyles({});

    const fullHistory = currentPuzzle.moveHistory;
    const fenAfterIncorrectMove = fullHistory[fullHistory.length - 1];
    const initialHistory = fullHistory.slice(0, fullHistory.length);

    try {
      const analysisResult = await getOneShotEvaluation(
        currentPuzzle.currentFen,
        { depth: SCAN_DEPTH }
      );

      const pvUci = analysisResult?.[0]?.pvUci;

      if (!pvUci) {
        console.error("Could not get the best line for blunder analysis.");
        setIsShowingBlunderAnalysis(false);
        return;
      }

      const uciMoves = pvUci.split(" ").slice(0, 10);
      const generatedFens = [];
      let currentFenForPlayback = fenAfterIncorrectMove;

      for (const uciMove of uciMoves) {
        if (!isShowingBlunderAnalysisRef.current) {
          console.log("Blunder analysis was cancelled.");
          return;
        }

        const tempGame = new Chess(currentFenForPlayback);
        const move = tempGame.move(uciMove, {
          sloppy: true,
        });

        if (!move) {
          console.warn(
            `Blunder analysis: Invalid move ${uciMove} in PV. Stopping playback.`
          );
          break;
        }

        playSoundForMove(move);
        currentFenForPlayback = tempGame.fen();
        generatedFens.push(currentFenForPlayback);

        setPuzzleStates((prev) => {
          const newStates = [...prev];
          const p = { ...newStates[currentPuzzleIndex] };
          if (!p) return prev;
          p.currentFen = currentFenForPlayback;
          newStates[currentPuzzleIndex] = p;
          return newStates;
        });

        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      setPuzzleStates((prev) => {
        const newStates = [...prev];
        const p = { ...newStates[currentPuzzleIndex] };
        if (!p) return prev;
        p.moveHistory = [...initialHistory, ...generatedFens];
        p.currentMoveIndex = initialHistory.length - 1 + generatedFens.length;
        p.currentFen = currentFenForPlayback;
        newStates[currentPuzzleIndex] = p;
        return newStates;
      });
    } catch (error) {
      console.error("Error during blunder analysis:", error);
    } finally {
      if (isShowingBlunderAnalysisRef.current) {
        setIsShowingBlunderAnalysis(false);
      }
    }
  }, [currentPuzzle]);

  const handleResetBoard = useCallback(() => {
    setLastMoveDisplay(null);
    setHasInitiatedContinuePlaying(false);
    setHasBeenReset(true);
    setPuzzleHasBeenFailedOnce(false);
    setPuzzleHasBeenSolvedOnce(false);
    setIsShowingMoreMovesActive(false);
    setIsContinuePlayingActive(false);
    setHasActivatedLiveAnalysis(false);
    setHasInitiatedSelfAnalysis(false);
    setHasShownCounter(false);
    SetPersonalisedRatingChange(null);
    setHintsUsedPerMove([]);
    cleanupMoveState();
    setShowLiveAnalysisPanel(false);
    setPuzzleStates((prevStates) => {
      const newStates = [...prevStates];
      newStates[currentPuzzleIndex] = resetPuzzleInteractiveState(
        newStates[currentPuzzleIndex]
      );
      return newStates;
    });
  }, [currentPuzzleIndex, resetPuzzleInteractiveState]);

  const handleHintClick = useCallback(async () => {
    if (!currentPuzzle || currentMoveHintLevel >= 2 || isHintLoading) return;

    const isPuzzleActive =
      !currentPuzzle.blunderDetails.puzzleSolved &&
      !currentPuzzle.blunderDetails.incorrectMove;
    if (!isPuzzleActive) return;

    if (currentMoveHintLevel === 0) {
      let hintMoveObject = null;
      let nextMoveSan = null;

      const solutionPlyIndex = currentPuzzle.isPuzzleInProgress
        ? currentPuzzle.activeVariationPlyIndex + 1
        : 0;

      if (currentPuzzle.activeVariationPath) {
        nextMoveSan = currentPuzzle.activeVariationPath[solutionPlyIndex];
      } else if (solutionPlyIndex === 0) {
        nextMoveSan = currentPuzzle.blunderDetails.topVariations?.[0]?.[0]?.san;
      }

      if (nextMoveSan) {
        const game = new Chess(currentPuzzle.currentFen);
        hintMoveObject = game
          .moves({ verbose: true })
          .find((m) => m.san === nextMoveSan);
      }

      if (!hintMoveObject) {
        console.log(
          "[Hint] Static hint not found or invalid for current position. Requesting dynamic hint."
        );
        setIsHintLoading(true);
        try {
          const analysisResult = await getOneShotEvaluation(
            currentPuzzle.currentFen,
            { depth: SCAN_DEPTH }
          );
          if (analysisResult && analysisResult[0]?.bestMoveUci) {
            const bestMoveUci = analysisResult[0].bestMoveUci;
            const game = new Chess(currentPuzzle.currentFen);
            hintMoveObject = game
              .moves({ verbose: true })
              .find(
                (m) =>
                  m.from === bestMoveUci.slice(0, 2) &&
                  m.to === bestMoveUci.slice(2, 4)
              );
          }
        } catch (error) {
          console.error("Error getting dynamic hint:", error);
        } finally {
          setIsHintLoading(false);
        }
      }

      if (hintMoveObject) {
        setHintMove({ from: hintMoveObject.from, to: hintMoveObject.to });
        setCurrentMoveHintLevel(1);
        const userMoveIndex = Math.floor(solutionPlyIndex / 2);
        setHintsUsedPerMove((prev) => {
          const newHints = [...prev];
          while (newHints.length <= userMoveIndex) {
            newHints.push(0);
          }
          newHints[userMoveIndex] = 1;
          return newHints;
        });
      } else {
        console.warn("Could not find a hint move from any source.");
      }
    } else if (currentMoveHintLevel === 1) {
      setCurrentMoveHintLevel(2);
      const solutionPlyIndex = currentPuzzle.isPuzzleInProgress
        ? currentPuzzle.activeVariationPlyIndex + 1
        : 0;
      const userMoveIndex = Math.floor(solutionPlyIndex / 2);
      setHintsUsedPerMove((prev) => {
        const newHints = [...prev];
        if (newHints[userMoveIndex] !== 2) {
          newHints[userMoveIndex] = 2;
        }
        return newHints;
      });
    }
  }, [
    currentPuzzle,
    currentMoveHintLevel,
    isHintLoading,
    getOneShotEvaluation,
  ]);

  const handlePieceDrop = useCallback(
    async (sourceSquare, targetSquare, piece) => {
      if (isShowingMoreMovesActive) return false;

      const currentPuzzle = puzzleStates[currentPuzzleIndex];
      if (!currentPuzzle) {
        return false;
      }

      const gameCheck = new Chess(currentPuzzle.currentFen);
      const possibleMoves = gameCheck.moves({ verbose: true });
      const isPromotion = possibleMoves.find(
        (m) =>
          m.from === sourceSquare &&
          m.to === targetSquare &&
          m.flags.includes("p")
      );

      if (isPromotion && piece.length > 1) {
        setPromotionMove({ from: sourceSquare, to: targetSquare });
        setPromotionSquare(targetSquare);
        setPromotionDialogOpen(true);
        cleanupMoveState();
        return false;
      }
      if (isContinuePlayingActive) {
        const isNotOnLatestMove =
          currentPuzzle.currentMoveIndex !==
          currentPuzzle.moveHistory.length - 1;

        if (isNotOnLatestMove) {
          console.log(
            "[Continue Puzzle] Move blocked: You can only make moves from the latest position."
          );
          return false;
        }
        const gameAfterUserMove = new Chess(currentPuzzle.currentFen);
        let move;
        try {
          move = gameAfterUserMove.move({
            from: sourceSquare,
            to: targetSquare,
            promotion: piece,
          });
          if (!move) {
            playIllegalMoveSound();
            return false;
          }
          playSoundForMove(move);
        } catch (e) {
          playIllegalMoveSound();
          return false;
        }
        const { blunderPly, userColor } = currentPuzzle.blunderDetails;
        const userMoveIndex = (currentPuzzle.playedMovesSan || []).length;
        const movePly = blunderPly + 1 + userMoveIndex * 2;
        const moveNumber = Math.ceil(movePly / 2);
        const prefix =
          userColor === "w" ? `${moveNumber}. ` : `${moveNumber}. ..`;
        setLastMoveDisplay({ text: `${prefix}${move.san}`, status: "neutral" });
        const newFenAfterUserMove = gameAfterUserMove.fen();
        const updatedMoveIndex = currentPuzzle.currentMoveIndex + 1;
        console.log(`[continue puzzle] User played '${move.san}'.`);

        setPuzzleStates((prev) => {
          const newStates = [...prev];
          const p = { ...newStates[currentPuzzleIndex] };
          p.currentFen = newFenAfterUserMove;
          p.moveHistory = [...p.moveHistory, newFenAfterUserMove];
          p.playedMovesSan = [...(p.playedMovesSan || []), move.san];
          p.fullMoveSANs = [...(p.fullMoveSANs || []), move.san];
          p.currentMoveIndex = updatedMoveIndex;
          p.activeVariationPlyIndex++;
          newStates[currentPuzzleIndex] = p;
          return newStates;
        });

        if (gameAfterUserMove.isGameOver()) {
          console.log(
            "[continue puzzle] Game over after user move. Ending session."
          );

          playPuzzleSuccessSound();
          setIsContinuePlayingActive(false);
          setPuzzleStates((prev) => {
            const newStates = [...prev];
            const p = { ...newStates[currentPuzzleIndex] };
            p.blunderDetails.puzzleSolved = true;
            p.blunderDetails.lastMove = { from: move.from, to: move.to };
            p.isPuzzleInProgress = false;
            newStates[currentPuzzleIndex] = p;
            return newStates;
          });
          return true;
        }

        try {
          const userColor = currentPuzzle.blunderDetails.userColor;
          const evalBeforeUserMoveResults = await getOneShotEvaluation(
            currentPuzzle.currentFen,
            { depth: SCAN_DEPTH }
          );

          if (
            !evalBeforeUserMoveResults ||
            evalBeforeUserMoveResults.length === 0
          )
            throw new Error("Could not get eval before user move.");
          const evalBeforeUserMoveCp =
            evalBeforeUserMoveResults[0].evalScoreForComparison;

          const evalAfterUserMoveResults = await getOneShotEvaluation(
            newFenAfterUserMove,
            { depth: SCAN_DEPTH }
          );

          if (
            !evalAfterUserMoveResults ||
            evalAfterUserMoveResults.length === 0
          )
            throw new Error("Could not get eval after user move.");
          const evalAfterUserMoveCp =
            evalAfterUserMoveResults[0].evalScoreForComparison;

          const evalDiff =
            userColor === "w"
              ? evalAfterUserMoveCp - evalBeforeUserMoveCp
              : evalBeforeUserMoveCp - evalAfterUserMoveCp;

          const normalizedOriginalPrevEvalCpForUser =
            userColor === "w"
              ? currentPuzzle.blunderDetails.prevEvalCp
              : -currentPuzzle.blunderDetails.prevEvalCp;
          const absNormalizedOriginalPrevEvalCp = Math.abs(
            normalizedOriginalPrevEvalCpForUser
          );

          let baseThreshold = EVAL_DIFF_THRESHOLD;
          if (
            absNormalizedOriginalPrevEvalCp >= 600 &&
            absNormalizedOriginalPrevEvalCp < 1000
          )
            baseThreshold = 200;
          else if (
            absNormalizedOriginalPrevEvalCp >= 1000 &&
            absNormalizedOriginalPrevEvalCp <= 5000
          )
            baseThreshold = 250;

          const passThreshold = baseThreshold - EVAL_BUFFER;

          const prefix =
            userColor === "w" ? `${moveNumber}. ` : `${moveNumber}. ..`;

          if (evalDiff >= -passThreshold) {
            setLastMoveDisplay({
              text: `${prefix}${move.san}`,
              status: "correct",
            });
            const isFinalUserMove =
              updatedMoveIndex - continuePlayingStartMoveIndex.current >= 9;

            if (isFinalUserMove) {
              console.log(
                "[continue puzzle] Final user move is correct. Ending session."
              );
              playPuzzleSuccessSound();
              setIsContinuePlayingActive(false);
              setPuzzleStates((prev) => {
                const newStates = [...prev];
                const p = { ...newStates[currentPuzzleIndex] };
                p.blunderDetails.puzzleSolved = true;
                p.blunderDetails.lastMove = { from: move.from, to: move.to };
                p.isPuzzleInProgress = false;
                newStates[currentPuzzleIndex] = p;
                return newStates;
              });
              return true;
            }
            console.log("[continue puzzle] Getting computer response.");
            setPuzzleStates((prev) => {
              const newStates = [...prev];
              newStates[currentPuzzleIndex].awaitingDynamicEngineMove = true;
              return newStates;
            });
            startAnalysis(newFenAfterUserMove, "depth", SCAN_DEPTH, 1);
          } else {
            setLastMoveDisplay({
              text: `${prefix}${move.san}`,
              status: "incorrect",
            });
            console.log(
              `\x1b[31m[continue puzzle] Incorrect move. Eval Drop: ${evalDiff.toFixed(
                0
              )}cp > ${-passThreshold}cp. Ending session.\x1b[0m`
            );
            playPuzzleFailSound();
            const indexAtTimeOfRequest = currentPuzzleIndexRef.current;
            const fenBeforeBlunder = currentPuzzle.currentFen;

            getOneShotEvaluation(fenBeforeBlunder, {
              depth: SCAN_DEPTH,
            }).then((result) => {
              if (currentPuzzleIndexRef.current === indexAtTimeOfRequest) {
                if (result && result[0]?.bestMoveUci) {
                  const uci = result[0].bestMoveUci;
                  setBestMoveArrow({
                    from: uci.slice(0, 2),
                    to: uci.slice(2, 4),
                  });
                }
              } else {
                console.log(
                  "[Best Move Arrow] Stale analysis result for continue-play failure was ignored."
                );
              }
            });

            setIsContinuePlayingActive(false);
            setPuzzleStates((prev) => {
              const newStates = [...prev];
              const p = { ...newStates[currentPuzzleIndex] };
              p.continuePlayFailure = true;
              p.blunderDetails.lastMove = { from: move.from, to: move.to };
              p.isPuzzleInProgress = false;
              p.awaitingDynamicEngineMove = false;
              newStates[currentPuzzleIndex] = p;
              return newStates;
            });
          }
        } catch (dynamicEvalError) {
          console.error(
            "[continue puzzle] CRITICAL Error during dynamic analysis:",
            dynamicEvalError
          );
          playPuzzleFailSound();
          setIsContinuePlayingActive(false);
          setPuzzleStates((prev) => {
            const newStates = [...prev];
            const p = { ...newStates[currentPuzzleIndex] };
            p.blunderDetails.incorrectMove = true;
            p.blunderDetails.lastMove = { from: move.from, to: move.to };
            p.isPuzzleInProgress = false;
            newStates[currentPuzzleIndex] = p;
            return newStates;
          });
        }
        return true;
      }
      const isPuzzleOver =
        currentPuzzle.blunderDetails.puzzleSolved ||
        puzzleHasBeenFailedOnce ||
        currentPuzzle.continuePlayFailure;

      if (isPuzzleOver) {
        const gameForAnalysis = new Chess(currentPuzzle.currentFen);
        let moveResult;
        try {
          moveResult = gameForAnalysis.move({
            from: sourceSquare,
            to: targetSquare,
            promotion: piece,
          });
        } catch (error) {
          playIllegalMoveSound();
          console.log("[Analysis Mode] Illegal move error:", error);
          return false;
        }

        if (moveResult === null) {
          console.log("[Analysis Mode] Illegal move attempt.");
          playIllegalMoveSound();
          return false;
        }

        if (personalisedRatingChangeTimeoutRef.current) {
          clearTimeout(personalisedRatingChangeTimeoutRef.current);
        }
        SetPersonalisedRatingChange(null);
        setHasInitiatedSelfAnalysis(true);
        setCustomSquareStyles({});
        console.log("[Analysis Mode] Making a free move.");
        playSoundForMove(moveResult);

        const newFenForAnalysis = gameForAnalysis.fen();
        setPuzzleStates((prevStates) => {
          const newStates = [...prevStates];
          const puzzleToUpdate = { ...newStates[currentPuzzleIndex] };
          if (!puzzleToUpdate.analysisMoveTree) {
            const rootNode = {
              san: null,
              fen: puzzleToUpdate.currentFen,
              children: [],
              parent: null,
            };
            puzzleToUpdate.analysisMoveTree = rootNode;
            puzzleToUpdate.analysisCurrentNode = rootNode;
          }

          let currentNode = puzzleToUpdate.analysisCurrentNode;
          let nextNode = currentNode.children.find(
            (child) => child.san === moveResult.san
          );

          if (!nextNode) {
            nextNode = {
              san: moveResult.san,
              fen: newFenForAnalysis,
              children: [],
              parent: currentNode,
            };
            currentNode.children.push(nextNode);
          }
          puzzleToUpdate.analysisCurrentNode = nextNode;
          puzzleToUpdate.currentFen = newFenForAnalysis;
          const updatedMoveHistory = puzzleToUpdate.moveHistory.slice(
            0,
            puzzleToUpdate.currentMoveIndex + 1
          );
          updatedMoveHistory.push(newFenForAnalysis);

          const updatedSanHistory = (puzzleToUpdate.fullMoveSANs || []).slice(
            0,
            puzzleToUpdate.currentMoveIndex -
              puzzleToUpdate.puzzleInteractiveStartIndex
          );
          updatedSanHistory.push(moveResult.san);
          puzzleToUpdate.fullMoveSANs = updatedSanHistory;

          puzzleToUpdate.moveHistory = updatedMoveHistory;
          puzzleToUpdate.currentMoveIndex = updatedMoveHistory.length - 1;
          newStates[currentPuzzleIndex] = puzzleToUpdate;
          return newStates;
        });
        return true;
      }

      const gameBeforeUserMove = new Chess(currentPuzzle.currentFen);
      const isUsersTurn =
        gameBeforeUserMove.turn() === currentPuzzle.blunderDetails.userColor;
      if (!isUsersTurn) {
        const gameForAnalysis = new Chess(currentPuzzle.currentFen);
        let moveResult;
        try {
          moveResult = gameForAnalysis.move({
            from: sourceSquare,
            to: targetSquare,
            promotion: piece,
          });
        } catch (error) {
          playIllegalMoveSound();
          return false;
        }
        if (moveResult === null) {
          playIllegalMoveSound();
          return false;
        }

        if (personalisedRatingChangeTimeoutRef.current) {
          clearTimeout(personalisedRatingChangeTimeoutRef.current);
        }
        SetPersonalisedRatingChange(null);
        setCustomSquareStyles({});
        setHasInitiatedSelfAnalysis(true);
        playSoundForMove(moveResult);

        const newFenForAnalysis = gameForAnalysis.fen();
        setPuzzleStates((prevStates) => {
          const newStates = [...prevStates];
          const puzzleToUpdate = { ...newStates[currentPuzzleIndex] };

          const updatedMoveHistory = puzzleToUpdate.moveHistory.slice(
            0,
            puzzleToUpdate.currentMoveIndex + 1
          );
          updatedMoveHistory.push(newFenForAnalysis);

          const updatedSanHistory = (puzzleToUpdate.fullMoveSANs || []).slice(
            0,
            puzzleToUpdate.currentMoveIndex -
              puzzleToUpdate.puzzleInteractiveStartIndex
          );
          updatedSanHistory.push(moveResult.san);

          puzzleToUpdate.currentFen = newFenForAnalysis;
          puzzleToUpdate.moveHistory = updatedMoveHistory;
          puzzleToUpdate.fullMoveSANs = updatedSanHistory;
          puzzleToUpdate.currentMoveIndex = updatedMoveHistory.length - 1;

          newStates[currentPuzzleIndex] = puzzleToUpdate;
          return newStates;
        });
        return true;
      }
      const isNotOnLatestMove =
        currentPuzzle.currentMoveIndex !== currentPuzzle.moveHistory.length - 1;

      if (isNotOnLatestMove) {
        console.log(
          "[Puzzle Play] Move blocked: You can only make moves from the latest position while the puzzle is in progress."
        );
        return false;
      }

      if (currentPuzzle.awaitingDynamicEngineMove) {
        console.log(
          `[Puzzle Play] Cannot make moves: awaitingDynamicEngineMove=true`
        );
        return false;
      }
      let potentialMove;
      try {
        potentialMove = gameBeforeUserMove.move({
          from: sourceSquare,
          to: targetSquare,
          promotion: piece,
        });

        if (potentialMove === null) {
          playIllegalMoveSound();
          console.log(
            `[Puzzle Play] Chess.js considers move ${sourceSquare}-${targetSquare} invalid.`
          );
          return false;
        }
        playSoundForMove(potentialMove);
      } catch (error) {
        playIllegalMoveSound();
        console.log(
          `[Puzzle Play] Illegal move: ${sourceSquare}-${targetSquare}`,
          error
        );
        return false;
      }

      const newFenAfterUserMove = gameBeforeUserMove.fen();
      const userPlayedSan = potentialMove.san;
      const { blunderPly, userColor } = currentPuzzle.blunderDetails;
      const userMoveIndex = (currentPuzzle.playedMovesSan || []).length;
      const movePly = blunderPly + 1 + userMoveIndex * 2;
      const moveNumber = Math.ceil(movePly / 2);
      const prefix =
        userColor === "w" ? `${moveNumber}. ` : `${moveNumber}. ..`;

      setPuzzleStates((prevStates) => {
        const newStates = [...prevStates];
        const originalPuzzle = newStates[currentPuzzleIndex];

        const updatedSanHistory = (originalPuzzle.fullMoveSANs || []).slice(
          0,
          originalPuzzle.currentMoveIndex -
            originalPuzzle.puzzleInteractiveStartIndex
        );
        updatedSanHistory.push(userPlayedSan);

        const puzzleToUpdate = {
          ...originalPuzzle,
          currentFen: newFenAfterUserMove,
          moveHistory: [
            ...originalPuzzle.moveHistory.slice(
              0,
              originalPuzzle.currentMoveIndex + 1
            ),
            newFenAfterUserMove,
          ],
          playedMovesSan: [
            ...(originalPuzzle.playedMovesSan || []),
            userPlayedSan,
          ],
          fullMoveSANs: updatedSanHistory,
          currentMoveIndex: originalPuzzle.currentMoveIndex + 1,
          isPuzzleInProgress: false,
          awaitingDynamicEngineMove: false,
          blunderDetails: {
            ...originalPuzzle.blunderDetails,
            puzzleSolved: false,
            incorrectMove: false,
          },
        };
        newStates[currentPuzzleIndex] = puzzleToUpdate;
        return newStates;
      });

      const isFirstMoveOfPuzzleAttempt = !currentPuzzle.isPuzzleInProgress;

      if (isFirstMoveOfPuzzleAttempt) {
        const originalBlunderSan = currentPuzzle.blunderDetails.blunderMoveSan;

        if (userPlayedSan === originalBlunderSan) {
          console.log(
            "[Puzzle Logic] User played the exact blunder move. Rejecting immediately."
          );
          playPuzzleFailSound();

          setLastMoveDisplay({
            text: `${prefix}${userPlayedSan}`,
            status: "incorrect",
          });

          setPuzzleStates((prev) => {
            const newStates = [...prev];
            const p = { ...newStates[currentPuzzleIndex] };
            p.blunderDetails.incorrectMove = true;
            p.isRepeatedBlunder = true;
            p.blunderDetails.lastMove = {
              from: potentialMove.from,
              to: potentialMove.to,
            };
            p.isPuzzleInProgress = false;
            newStates[currentPuzzleIndex] = p;
            return newStates;
          });

          return true;
        }
      }
      let matchedVariation = null;

      if (isFirstMoveOfPuzzleAttempt) {
        for (const variation of currentPuzzle.blunderDetails.topVariations) {
          if (variation.length > 0 && variation[0].san === userPlayedSan) {
            matchedVariation = variation;
            break;
          }
        }
      } else {
        const expectedUserMoveIndexInPath =
          currentPuzzle.activeVariationPlyIndex + 1;
        if (
          currentPuzzle.activeVariationPath &&
          expectedUserMoveIndexInPath <
            currentPuzzle.activeVariationPath.length &&
          currentPuzzle.activeVariationPath[expectedUserMoveIndexInPath] ===
            userPlayedSan
        ) {
          matchedVariation = currentPuzzle.blunderDetails.topVariations.find(
            (v) =>
              v.length > 0 && v[0].san === currentPuzzle.activeVariationPath[0]
          );
        }
      }

      if (matchedVariation) {
        setLastMoveDisplay({
          text: `${prefix}${userPlayedSan}`,
          status: "correct",
        });

        const currentUserPly = isFirstMoveOfPuzzleAttempt
          ? 0
          : currentPuzzle.activeVariationPlyIndex + 1;

        const targetPlyCount = calculateMaxPliesForDisplay(
          matchedVariation,
          currentPuzzle.blunderDetails,
          EVAL_DIFF_THRESHOLD
        );

        console.log(
          `[Puzzle Logic] Correct move in pre-analyzed variation. User Ply: ${currentUserPly}. Target Plies: ${targetPlyCount}.`
        );

        const isPuzzleComplete = currentUserPly + 1 >= targetPlyCount;

        if (isPuzzleComplete) {
          playPuzzleSuccessSound();
          console.log("[Puzzle Logic] Final move played. Puzzle solved!");
          setPuzzleStates((prev) => {
            const newStates = [...prev];
            const p = { ...newStates[currentPuzzleIndex] };
            p.blunderDetails.puzzleSolved = true;
            p.blunderDetails.lastMove = {
              from: potentialMove.from,
              to: potentialMove.to,
            };
            p.isPuzzleInProgress = false;
            p.activeVariationPath = matchedVariation.map((m) => m.san);
            p.activeVariationPlyIndex = currentUserPly;
            newStates[currentPuzzleIndex] = p;
            return newStates;
          });
        } else {
          const computerMoveSan = matchedVariation[currentUserPly + 1]?.san;
          if (computerMoveSan) {
            console.log(
              `[Puzzle Logic] Puzzle continues. Computer will play: ${computerMoveSan}`
            );
            setPuzzleStates((prev) => {
              const newStates = [...prev];
              const p = { ...newStates[currentPuzzleIndex] };
              p.isPuzzleInProgress = true;
              p.activeVariationPath = matchedVariation.map((m) => m.san);
              p.activeVariationPlyIndex = currentUserPly;
              p.targetPlyCountForSolve = targetPlyCount;
              newStates[currentPuzzleIndex] = p;
              return newStates;
            });
            applyComputerMove(
              computerMoveSan,
              currentPuzzle,
              currentPuzzleIndex,
              newFenAfterUserMove,
              setPuzzleStates,
              "pre-analyzed"
            );
          } else {
            playPuzzleSuccessSound();
            console.warn(
              `[Puzzle Logic] Pre-analyzed variation ended prematurely. Marking as solved.`
            );
            setPuzzleStates((prev) => {
              const newStates = [...prev];
              const p = { ...newStates[currentPuzzleIndex] };
              p.blunderDetails.puzzleSolved = true;
              p.blunderDetails.lastMove = {
                from: potentialMove.from,
                to: potentialMove.to,
              };
              p.isPuzzleInProgress = false;
              newStates[currentPuzzleIndex] = p;
              return newStates;
            });
          }
        }
      } else {
        console.log(
          `[Puzzle Logic] Move not in pre-analyzed variations. Performing dynamic check.`
        );
        try {
          if (gameBeforeUserMove.isCheckmate()) {
            playPuzzleSuccessSound();
            console.log("[Puzzle Logic] Dynamic checkmate! Puzzle solved.");
            setPuzzleStates((prev) => {
              const newStates = [...prev];
              const p = { ...newStates[currentPuzzleIndex] };
              p.blunderDetails.puzzleSolved = true;
              p.blunderDetails.lastMove = {
                from: potentialMove.from,
                to: potentialMove.to,
              };
              p.isPuzzleInProgress = false;
              newStates[currentPuzzleIndex] = p;
              return newStates;
            });
            return true;
          }

          const evalBeforeResults = await getOneShotEvaluation(
            currentPuzzle.currentFen,
            { depth: SCAN_DEPTH }
          );
          const evalAfterResults = await getOneShotEvaluation(
            newFenAfterUserMove,
            { depth: SCAN_DEPTH }
          );

          if (!evalBeforeResults?.[0] || !evalAfterResults?.[0]) {
            throw new Error("Could not get evaluation for dynamic check.");
          }

          const evalBeforeCp = evalBeforeResults[0].evalScoreForComparison;
          const evalAfterCp = evalAfterResults[0].evalScoreForComparison;
          const evalDiff =
            userColor === "w"
              ? evalAfterCp - evalBeforeCp
              : evalBeforeCp - evalAfterCp;

          const passThreshold = EVAL_DIFF_THRESHOLD - EVAL_BUFFER;

          const isMoveCorrect = evalDiff >= -passThreshold;

          if (isMoveCorrect) {
            setLastMoveDisplay({
              text: `${prefix}${userPlayedSan}`,
              status: "correct",
            });

            const currentUserPly = isFirstMoveOfPuzzleAttempt
              ? 0
              : currentPuzzle.activeVariationPlyIndex + 1;
            const mainLineVariation =
              currentPuzzle.blunderDetails.topVariations[0];
            const targetPlyCount =
              currentPuzzle.targetPlyCountForSolve > 0
                ? currentPuzzle.targetPlyCountForSolve
                : mainLineVariation
                ? calculateMaxPliesForDisplay(
                    mainLineVariation,
                    currentPuzzle.blunderDetails,
                    EVAL_DIFF_THRESHOLD
                  )
                : 3;

            console.log(
              `[Puzzle Logic] Dynamic move is correct. User Ply: ${currentUserPly}. Target Plies: ${targetPlyCount}.`
            );

            const isPuzzleComplete = currentUserPly + 1 >= targetPlyCount;

            if (isPuzzleComplete) {
              playPuzzleSuccessSound();
              console.log(
                "[Puzzle Logic] Final dynamic move played. Puzzle solved!"
              );
              setPuzzleStates((prev) => {
                const newStates = [...prev];
                const p = { ...newStates[currentPuzzleIndex] };
                p.blunderDetails.puzzleSolved = true;
                p.blunderDetails.lastMove = {
                  from: potentialMove.from,
                  to: potentialMove.to,
                };
                p.isPuzzleInProgress = false;
                p.activeVariationPath = null;
                newStates[currentPuzzleIndex] = p;
                return newStates;
              });
            } else {
              console.log(
                "[Puzzle Logic] Dynamic move correct. Requesting computer response."
              );
              setPuzzleStates((prev) => {
                const newStates = [...prev];
                const p = { ...newStates[currentPuzzleIndex] };
                p.isPuzzleInProgress = true;
                p.awaitingDynamicEngineMove = true;
                p.activeVariationPath = null;
                p.activeVariationPlyIndex = currentUserPly;
                p.targetPlyCountForSolve = targetPlyCount;
                newStates[currentPuzzleIndex] = p;
                return newStates;
              });
              startAnalysis(newFenAfterUserMove, "depth", SCAN_DEPTH, 1);
            }
          } else {
            playPuzzleFailSound();
            setLastMoveDisplay({
              text: `${prefix}${userPlayedSan}`,
              status: "incorrect",
            });
            console.log(
              `\x1b[31m[Puzzle Logic] Dynamic move is incorrect. Eval Drop: ${evalDiff.toFixed(
                0
              )}cp\x1b[0m`
            );

            const indexAtTimeOfRequest = currentPuzzleIndexRef.current;

            getOneShotEvaluation(currentPuzzle.currentFen, {
              depth: SCAN_DEPTH,
            }).then((result) => {
              if (currentPuzzleIndexRef.current === indexAtTimeOfRequest) {
                if (result?.[0]?.bestMoveUci) {
                  const uci = result[0].bestMoveUci;
                  setBestMoveArrow({
                    from: uci.slice(0, 2),
                    to: uci.slice(2, 4),
                  });
                }
              } else {
                console.log(
                  "[Best Move Arrow] Stale analysis result for incorrect move was ignored."
                );
              }
            });

            setPuzzleStates((prev) => {
              const newStates = [...prev];
              const p = { ...newStates[currentPuzzleIndex] };
              p.blunderDetails.incorrectMove = true;
              p.blunderDetails.lastMove = {
                from: potentialMove.from,
                to: potentialMove.to,
              };
              p.isPuzzleInProgress = false;
              newStates[currentPuzzleIndex] = p;
              return newStates;
            });
          }
        } catch (dynamicEvalError) {
          console.error(
            "[Puzzle Logic] CRITICAL Error during dynamic analysis:",
            dynamicEvalError
          );
          playPuzzleFailSound();
          setPuzzleStates((prev) => {
            const newStates = [...prev];
            const p = { ...newStates[currentPuzzleIndex] };
            p.blunderDetails.incorrectMove = true;
            p.blunderDetails.lastMove = {
              from: potentialMove.from,
              to: potentialMove.to,
            };
            p.isPuzzleInProgress = false;
            newStates[currentPuzzleIndex] = p;
            return newStates;
          });
        }
      }
      return true;
    },
    [
      currentPuzzleIndex,
      puzzleStates,
      isContinuePlayingActive,
      isShowingMoreMovesActive,
      startAnalysis,
      applyComputerMove,
      getOneShotEvaluation,
    ]
  );

  const handlePreviousMove = useCallback(() => {
    cleanupMoveState();
    setCustomSquareStyles({});
    setPuzzleStates((prevStates) => {
      const newStates = [...prevStates];
      const puzzle = newStates[currentPuzzleIndex];
      if (
        puzzle &&
        puzzle.currentMoveIndex > puzzle.puzzleInteractiveStartIndex
      ) {
        const fenBefore = puzzle.moveHistory[puzzle.currentMoveIndex - 1];
        const fenAfter = puzzle.moveHistory[puzzle.currentMoveIndex];
        const tempGame = new Chess(fenBefore);
        const legalMoves = tempGame.moves({ verbose: true });

        for (const move of legalMoves) {
          const tempGameCopy = new Chess(fenBefore);
          tempGameCopy.move(move);
          if (tempGameCopy.fen() === fenAfter) {
            playSoundForMove(move);
            break;
          }
        }

        const newMoveIndex = puzzle.currentMoveIndex - 1;
        const puzzleToUpdate = {
          ...puzzle,
          currentFen: puzzle.moveHistory[newMoveIndex],
          currentMoveIndex: newMoveIndex,
        };

        if (puzzleToUpdate.analysisCurrentNode?.parent) {
          puzzleToUpdate.analysisCurrentNode =
            puzzleToUpdate.analysisCurrentNode.parent;
        }

        newStates[currentPuzzleIndex] = puzzleToUpdate;
      }
      return newStates;
    });
  }, [currentPuzzleIndex]);

  const handleNextMove = useCallback(() => {
    cleanupMoveState();
    setCustomSquareStyles({});
    setPuzzleStates((prevStates) => {
      const newStates = [...prevStates];
      const puzzle = newStates[currentPuzzleIndex];
      if (puzzle && puzzle.currentMoveIndex < puzzle.moveHistory.length - 1) {
        const fenBefore = puzzle.moveHistory[puzzle.currentMoveIndex];
        const fenAfter = puzzle.moveHistory[puzzle.currentMoveIndex + 1];
        const tempGame = new Chess(fenBefore);
        const legalMoves = tempGame.moves({ verbose: true });
        for (const move of legalMoves) {
          const tempGameCopy = new Chess(fenBefore);
          tempGameCopy.move(move);
          if (tempGameCopy.fen() === fenAfter) {
            playSoundForMove(move);
            break;
          }
        }

        const newMoveIndex = puzzle.currentMoveIndex + 1;
        const puzzleToUpdate = {
          ...puzzle,
          currentFen: puzzle.moveHistory[newMoveIndex],
          currentMoveIndex: newMoveIndex,
        };
        if (puzzleToUpdate.analysisCurrentNode) {
          const nextNode = puzzleToUpdate.analysisCurrentNode.children.find(
            (child) => child.fen === puzzleToUpdate.currentFen
          );
          if (nextNode) {
            puzzleToUpdate.analysisCurrentNode = nextNode;
          }
        }

        newStates[currentPuzzleIndex] = puzzleToUpdate;
      }
      return newStates;
    });
  }, [currentPuzzleIndex]);

  const fetchMorePuzzles = useCallback(async () => {
    if (!username) return;

    const currentIds = puzzleStates.map((p) => p.id).join(",");
    const modeParam = isArchiveMode ? "&mode=archive" : "";

    console.log("[Puzzles] Fetching next batch...");
    try {
      const response = await axios.get(
        `/get-personalized-puzzles/${username}?limit=30&excludeIds=${currentIds}${modeParam}`
      );

      const newPuzzlesRaw = response.data;
      if (newPuzzlesRaw.length > 0) {
        const newPuzzlesFormatted = newPuzzlesRaw.map((p) =>
          formatDbPuzzleToStateStructure(p)
        );

        setPuzzleStates((prev) => [...prev, ...newPuzzlesFormatted]);
        console.log(
          `[Puzzles] Appended ${newPuzzlesFormatted.length} new puzzles.`
        );

        setTimeout(() => {
          navigate(`?puzzle=${puzzleStates.length + 1}${modeParam}`, {
            replace: true,
          });
        }, 100);
      }
    } catch (e) {
      console.error("Error fetching more puzzles:", e);
    }
  }, [username, puzzleStates, navigate, isArchiveMode]);

  const handleNextPuzzle = () => {
    const modeStr = isArchiveMode ? "&mode=archive" : "";

    if (currentPuzzleIndex === puzzleStates.length - 1) {
      fetchMorePuzzles();
    } else {
      const newIndex = currentPuzzleIndex + 1;
      navigate(`?puzzle=${newIndex + 1}${modeStr}`, { replace: true });
    }
  };

  const handlePreviousPuzzle = () => {
    const modeStr = isArchiveMode ? "&mode=archive" : "";
    const newIndex = Math.max(currentPuzzleIndex - 1, 0);

    if (newIndex !== currentPuzzleIndex) {
      navigate(`?puzzle=${newIndex + 1}${modeStr}`, { replace: true });
    }
  };

  const formatAnalysisMoves = (puzzle) => {
    if (!puzzle?.analysisMoveTree) {
      return null;
    }

    const gameForMoveNumbers = new Chess(puzzle.analysisMoveTree.fen);
    const initialMoveNumber = gameForMoveNumbers.moveNumber();
    const initialTurn = gameForMoveNumbers.turn();

    function formatRecursive(node, moveNumber, turn) {
      if (!node?.children?.length) {
        return "";
      }

      const mainMoveNode = node.children[0];
      const sideVariationNodes = node.children.slice(1);

      const nextTurn = turn === "w" ? "b" : "w";
      const nextMoveNumber = turn === "w" ? moveNumber : moveNumber + 1;

      let mainMovePrefix = "";
      if (turn === "w") {
        mainMovePrefix = `${moveNumber}. `;
      } else {
        if (node === puzzle.analysisMoveTree) {
          mainMovePrefix = `${moveNumber}... `;
        }
      }
      const mainMoveSan = mainMoveNode.san;

      let variationsText = "";
      sideVariationNodes.forEach((varNode) => {
        let varPrefix = "";
        if (turn === "w") {
          varPrefix = `${moveNumber}. `;
        } else {
          varPrefix = `${moveNumber}... `;
        }

        const restOfVar = formatRecursive(varNode, nextMoveNumber, nextTurn);
        variationsText += `( ${varPrefix}${varNode.san} ${restOfVar} ) `;
      });

      const restOfMainLine = formatRecursive(
        mainMoveNode,
        nextMoveNumber,
        nextTurn
      );

      return `${mainMovePrefix}${mainMoveSan} ${variationsText}${restOfMainLine}`
        .replace(/\s+/g, " ")
        .trim();
    }

    const result = formatRecursive(
      puzzle.analysisMoveTree,
      initialMoveNumber,
      initialTurn
    );

    return result;
  };
  const formatPuzzleMovesForAnalysis = (puzzle) => {
    if (
      !puzzle ||
      puzzle.currentMoveIndex <= puzzle.puzzleInteractiveStartIndex
    ) {
      return "";
    }

    const startingFen = puzzle.moveHistory[puzzle.puzzleInteractiveStartIndex];
    const game = new Chess(startingFen);

    const output = [];

    for (
      let i = puzzle.puzzleInteractiveStartIndex;
      i < puzzle.currentMoveIndex;
      i++
    ) {
      const fenBeforeMove = puzzle.moveHistory[i];
      const fenAfterMove = puzzle.moveHistory[i + 1];

      const tempGame = new Chess(fenBeforeMove);
      const legalMoves = tempGame.moves({ verbose: true });

      const playedMove = legalMoves.find((move) => {
        tempGame.move(move.san);
        const found = tempGame.fen() === fenAfterMove;
        tempGame.undo();
        return found;
      });

      if (playedMove) {
        const moveNumber = game.moveNumber();
        const turn = game.turn();
        let prefix = "";

        if (turn === "w") {
          prefix = `${moveNumber}. `;
        } else if (output.length === 0) {
          prefix = `${moveNumber}... `;
        }

        output.push(`${prefix}${playedMove.san}`);

        game.move(playedMove.san);
      } else {
        console.warn(
          `Could not determine SAN for FEN transition at index ${i}`
        );
      }
    }

    return output.join(" ") + " ";
  };

  const statusMessage = puzzleStatusMessages({
    loadingGames,
    loadingPuzzles,
    loadingProcessedGames,
    error,
    username,
    chesscomUsername,
    lichessUsername,
    games,
    puzzleStates,
    loadingRating,
    analyzing: isGeneratingPuzzles,
    isEngineReady: isSocketConnected,
    onOpenArchive: handleOpenArchive,
    onImportPgn: handleOpenImportModal,
  });

  const handleContinuePlaying = useCallback(() => {
    if (!currentPuzzle || !currentPuzzle.blunderDetails.puzzleSolved) return;

    setHasInitiatedContinuePlaying(true);
    setIsContinuePlayingActive(true);
    setCustomSquareStyles({});
    SetPersonalisedRatingChange(null);

    const latestHistory = currentPuzzle.moveHistory;
    const fenToContinueFrom = latestHistory[latestHistory.length - 1];
    const moveIndexToContinueFrom = latestHistory.length - 1;

    const lastUserPly = currentPuzzle.activeVariationPlyIndex;
    const computerPlyIndex = lastUserPly + 1;
    let computerMoveSan = null;
    let isDynamic = false;

    if (
      currentPuzzle.activeVariationPath &&
      computerPlyIndex < currentPuzzle.activeVariationPath.length
    ) {
      computerMoveSan = currentPuzzle.activeVariationPath[computerPlyIndex];
      console.log(
        `[Continue Puzzle] Found next move in pre-analyzed path: ${computerMoveSan}`
      );
    } else {
      console.log(
        `[Continue Puzzle] Path ended or was dynamic. Triggering engine analysis.`
      );
      isDynamic = true;
    }

    if (!isDynamic && computerMoveSan) {
      setPuzzleStates((prev) => {
        const newStates = [...prev];
        const p = { ...newStates[currentPuzzleIndex] };
        p.currentFen = fenToContinueFrom;
        p.currentMoveIndex = moveIndexToContinueFrom;
        p.blunderDetails.puzzleSolved = false;
        p.isPuzzleInProgress = true;
        continuePlayingStartMoveIndex.current = moveIndexToContinueFrom;
        newStates[currentPuzzleIndex] = p;
        return newStates;
      });

      setTimeout(() => {
        const game = new Chess(fenToContinueFrom);
        const moveResult = game.move(computerMoveSan);

        if (moveResult) {
          playSoundForMove(moveResult);
          const fenAfterComputerMove = game.fen();

          setPuzzleStates((prev) => {
            const newStates = [...prev];
            const puzzleToUpdate = { ...newStates[currentPuzzleIndex] };

            if (puzzleToUpdate.currentFen !== fenToContinueFrom) {
              console.warn(
                "[Continue Puzzle - Delayed] State changed before move could be applied. Aborting."
              );
              return prev;
            }

            puzzleToUpdate.currentFen = fenAfterComputerMove;
            puzzleToUpdate.moveHistory = [
              ...latestHistory,
              fenAfterComputerMove,
            ];
            puzzleToUpdate.currentMoveIndex = moveIndexToContinueFrom + 1;
            newStates[currentPuzzleIndex] = puzzleToUpdate;
            return newStates;
          });
        }
      }, 800);
    } else {
      setPuzzleStates((prev) => {
        const newStates = [...prev];
        const p = { ...newStates[currentPuzzleIndex] };
        p.currentFen = fenToContinueFrom;
        p.currentMoveIndex = moveIndexToContinueFrom;
        p.blunderDetails.puzzleSolved = false;
        p.isPuzzleInProgress = true;
        p.awaitingDynamicEngineMove = true;
        continuePlayingStartMoveIndex.current = moveIndexToContinueFrom;
        newStates[currentPuzzleIndex] = p;
        return newStates;
      });
      startAnalysis(fenToContinueFrom, "depth", SCAN_DEPTH, 1);
    }
  }, [currentPuzzle, currentPuzzleIndex, startAnalysis]);
  const handleShowMoreMoves = useCallback(async () => {
    if (
      !currentPuzzle ||
      !currentPuzzle.blunderDetails.incorrectMove ||
      isShowingMoreMovesActiveRef.current
    ) {
      return;
    }
    setLastMoveDisplay(null);
    setBestMoveArrow(null);
    setIsShowingMoreMovesActive(true);
    setPuzzleHasBeenFailedOnce(true);
    SetPersonalisedRatingChange(null);
    setCustomSquareStyles({});

    const fullHistory = currentPuzzle.moveHistory;
    const fenBeforeIncorrectMove = fullHistory[fullHistory.length - 2];
    const initialHistory = fullHistory.slice(0, fullHistory.length - 1);
    const initialMoveIndex = fullHistory.length - 2;

    const rootNode = {
      san: null,
      fen: fenBeforeIncorrectMove,
      children: [],
      parent: null,
    };
    let currentNode = rootNode;

    setPuzzleStates((prev) => {
      const newStates = [...prev];
      const p = { ...newStates[currentPuzzleIndex] };
      p.currentFen = fenBeforeIncorrectMove;
      p.moveHistory = initialHistory;
      p.fullMoveSANs = (p.fullMoveSANs || []).slice(0, -1);
      p.currentMoveIndex = initialMoveIndex;
      p.blunderDetails.incorrectMove = false;
      p.blunderDetails.lastMove = null;
      p.analysisMoveTree = rootNode;
      p.analysisCurrentNode = rootNode;
      newStates[currentPuzzleIndex] = p;
      return newStates;
    });

    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const analysisResult = await getOneShotEvaluation(
        fenBeforeIncorrectMove,
        { depth: SCAN_DEPTH }
      );
      const pvUci = analysisResult?.[0]?.pvUci;

      if (!pvUci) {
        console.error("Could not get the best line for 'show best moves'.");
        setIsShowingMoreMovesActive(false);
        return;
      }

      const uciMoves = pvUci.split(" ").slice(0, 10);
      const generatedFens = [];
      let currentFenForPlayback = fenBeforeIncorrectMove;

      for (const uciMove of uciMoves) {
        if (!isShowingMoreMovesActiveRef.current) {
          console.log("'show best moves' was cancelled.");
          return;
        }

        const tempGame = new Chess(currentFenForPlayback);
        const move = tempGame.move(uciMove, { sloppy: true });

        if (!move) {
          console.warn(
            `'show best moves': Invalid move ${uciMove} in PV. Stopping playback.`
          );
          break;
        }

        playSoundForMove(move);
        currentFenForPlayback = tempGame.fen();
        generatedFens.push(currentFenForPlayback);

        const newNode = {
          san: move.san,
          fen: currentFenForPlayback,
          children: [],
          parent: currentNode,
        };
        currentNode.children.push(newNode);
        currentNode = newNode;

        setPuzzleStates((prev) => {
          const newStates = [...prev];
          const p = { ...newStates[currentPuzzleIndex] };
          if (!p) return prev;
          p.currentFen = currentFenForPlayback;
          newStates[currentPuzzleIndex] = p;
          return newStates;
        });

        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      setPuzzleStates((prev) => {
        const newStates = [...prev];
        const p = { ...newStates[currentPuzzleIndex] };
        if (!p) return prev;
        p.moveHistory = [...initialHistory, ...generatedFens];
        p.currentMoveIndex = initialMoveIndex + generatedFens.length;
        p.currentFen = currentFenForPlayback;

        p.analysisMoveTree = rootNode;
        p.analysisCurrentNode = currentNode;

        newStates[currentPuzzleIndex] = p;
        return newStates;
      });
    } catch (error) {
      console.error("Error during 'show best moves':", error);
    } finally {
      if (isShowingMoreMovesActiveRef.current) {
        setIsShowingMoreMovesActive(false);
      }
    }
  }, [currentPuzzle, currentPuzzleIndex]);
  const handlePgnSubmit = useCallback(() => {
    if (!pgnInputText.trim()) return;

    try {
      const tempGame = new Chess();
      tempGame.loadPgn(pgnInputText);

      const header = tempGame.header();
      const whiteName = header["White"] || "Imported White";
      const blackName = header["Black"] || "Imported Black";

      const knownAliases = [username, chesscomUsername, lichessUsername]
        .filter(Boolean)
        .map((name) => name.toLowerCase());

      const isUserInGame =
        knownAliases.includes(whiteName.toLowerCase()) ||
        knownAliases.includes(blackName.toLowerCase());

      const targetChesscomUser = isUserInGame
        ? chesscomUsername || "ImportUser"
        : whiteName;

      const importedGame = {
        pgn: pgnInputText,
        url: `imported-${Date.now()}`,
        source: "manual-import",
        white: whiteName,
        black: blackName,
      };

      console.log(
        `[Import] Sending manual game. Target identity: ${targetChesscomUser}`
      );

      sendPuzzleGenerationCommand([importedGame], {
        username,
        chesscomUsername: targetChesscomUser,
        lichessUsername: lichessUsername || "ImportUser",
      });

      handleCloseImportModal();
    } catch (e) {
      setImportError("Invalid PGN format. Please ensure the PGN is correct.");
      console.log("PGN Parse Error:", e);
    }
  }, [
    pgnInputText,
    sendPuzzleGenerationCommand,
    username,
    chesscomUsername,
    lichessUsername,
    handleCloseImportModal,
  ]);

  useEffect(() => {
    if (currentPuzzle?.blunderDetails.puzzleSolved) {
      setPuzzleHasBeenSolvedOnce(true);
    }

    const updateRatingOnSuccess = async () => {
      if (
        !username ||
        !currentPuzzle ||
        !currentPuzzle.blunderDetails.puzzleSolved ||
        ratingUpdatedForPuzzleIdRef.current === currentPuzzle.id ||
        hasInitiatedContinuePlaying ||
        hasActivatedLiveAnalysis ||
        hasInitiatedSelfAnalysis ||
        isArchiveMode
      ) {
        return;
      }

      console.log(
        `[Rating] Puzzle ${currentPuzzle.id} solved. Submitting for rating update.`
      );
      ratingUpdatedForPuzzleIdRef.current = currentPuzzle.id;

      const mainVariation = currentPuzzle.blunderDetails.topVariations[0];
      if (!mainVariation || !mainVariation.length) return;

      const mateIn = mainVariation[0]?.mateIn ?? null;

      const plyCount = calculateMaxPliesForDisplay(
        mainVariation,
        currentPuzzle.blunderDetails,
        EVAL_DIFF_THRESHOLD
      );

      const puzzleDetails = {
        prevEvalCp: currentPuzzle.blunderDetails.prevEvalCp,
        evalDropCp: currentPuzzle.blunderDetails.evalDropCp,
        userColor: currentPuzzle.blunderDetails.userColor,
        plyCount: plyCount,
        mateIn: mateIn,
        gameId: currentPuzzle.blunderDetails.gameId,
        blunderPly: currentPuzzle.blunderDetails.blunderPly,
      };

      try {
        const response = await axios.post(
          "/puzzles/update-personalised-rating",
          {
            username,
            puzzleDetails,
            success: true,
            hintsUsed: hintsUsedPerMove,
          }
        );
        const result = response.data;
        if (result.newRating) {
          const change = result.newRating - puzzleRating;
          SetPersonalisedRatingChange(change);
          setPuzzleRating(result.newRating);
          console.log(
            `[Rating] Frontend updated to new rating: ${result.newRating} (+${change})`
          );

          if (personalisedRatingChangeTimeoutRef.current) {
            clearTimeout(personalisedRatingChangeTimeoutRef.current);
          }

          personalisedRatingChangeTimeoutRef.current = setTimeout(() => {
            SetPersonalisedRatingChange(null);
          }, 3000);
        }
      } catch (err) {
        console.error("Network error updating puzzle rating:", err);
      }
    };

    updateRatingOnSuccess();

    return () => {
      if (personalisedRatingChangeTimeoutRef.current) {
        clearTimeout(personalisedRatingChangeTimeoutRef.current);
      }
    };
  }, [
    currentPuzzle?.blunderDetails.puzzleSolved,
    currentPuzzle?.id,
    currentPuzzleIndex,
    username,
    puzzleRating,
    hintsUsedPerMove,
    hasInitiatedContinuePlaying,
    hasActivatedLiveAnalysis,
    hasInitiatedSelfAnalysis,
    isArchiveMode,
  ]);

  useEffect(() => {
    let intervalId;

    if (isGeneratingPuzzles && username) {
      console.log("[Sync Effect] Generation active. Starting DB sync poll...");

      intervalId = setInterval(async () => {
        try {
          const puzzlesResponse = await axios.get(
            `/get-personalized-puzzles/${username}?limit=20`
          );
          const dbPuzzles = puzzlesResponse.data;
          const formattedPuzzles = dbPuzzles.map((p) =>
            formatDbPuzzleToStateStructure(p)
          );

          setPuzzleStates((prevStates) => {
            const existingIds = new Set(prevStates.map((p) => p.id));
            const newPuzzles = formattedPuzzles.filter(
              (p) => !existingIds.has(p.id)
            );
            if (newPuzzles.length > 0) {
              console.log(
                `[Sync Effect] Found ${newPuzzles.length} new puzzles.`
              );
              return [...prevStates, ...newPuzzles];
            }
            return prevStates;
          });

          if (pendingGameIdsRef.current.size > 0) {
            const processedResponse = await axios.get(
              `/get-processed-games/${username}`
            );

            const processedIds = processedResponse.data;
            const processedSet = new Set(processedIds);

            let allPendingAreProcessed = true;
            for (const id of pendingGameIdsRef.current) {
              if (!processedSet.has(id)) {
                allPendingAreProcessed = false;
                break;
              }
            }

            if (allPendingAreProcessed) {
              console.log(
                "[Sync Effect] All pending games marked as processed in DB. Stopping generation UI."
              );

              setProcessedGameIds((prev) => {
                const newSet = new Set(prev);
                pendingGameIdsRef.current.forEach((id) => newSet.add(id));
                return newSet;
              });

              pendingGameIdsRef.current.clear();
              stockfishSocketService.setGenerationActive(false);
            }
          }
        } catch (err) {
          console.warn("[Sync Effect] Poll failed:", err);
        }
      }, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isGeneratingPuzzles, username]);

  useEffect(() => {
    if (currentPuzzle?.blunderDetails.incorrectMove) {
      setPuzzleHasBeenFailedOnce(true);
    }

    const updateRatingOnFailure = async () => {
      if (
        !username ||
        !currentPuzzle ||
        !currentPuzzle.blunderDetails.incorrectMove ||
        ratingUpdatedForPuzzleIdRef.current === currentPuzzle.id ||
        hasActivatedLiveAnalysis ||
        hasInitiatedSelfAnalysis ||
        isArchiveMode
      ) {
        return;
      }

      console.log(
        `[Rating] Puzzle ${currentPuzzle.id} failed. Submitting for rating update.`
      );
      ratingUpdatedForPuzzleIdRef.current = currentPuzzle.id;

      const mainVariation = currentPuzzle.blunderDetails.topVariations[0];
      if (!mainVariation || !mainVariation.length) return;

      const mateIn = mainVariation[0]?.mateIn ?? null;

      const plyCount = calculateMaxPliesForDisplay(
        mainVariation,
        currentPuzzle.blunderDetails,
        EVAL_DIFF_THRESHOLD
      );

      const puzzleDetails = {
        prevEvalCp: currentPuzzle.blunderDetails.prevEvalCp,
        evalDropCp: currentPuzzle.blunderDetails.evalDropCp,
        userColor: currentPuzzle.blunderDetails.userColor,
        plyCount: plyCount,
        mateIn: mateIn,
        failPly: Math.floor(
          ((currentPuzzle.activeVariationPlyIndex ?? -1) + 1) / 2
        ),
        gameId: currentPuzzle.blunderDetails.gameId,
        blunderPly: currentPuzzle.blunderDetails.blunderPly,
      };
      try {
        const response = await axios.post(
          "/puzzles/update-personalised-rating",
          {
            username,
            puzzleDetails,
            success: false,
            hintsUsed: hintsUsedPerMove,
          }
        );

        const result = response.data;
        if (result.newRating) {
          const change = result.newRating - puzzleRating;
          SetPersonalisedRatingChange(change);
          setPuzzleRating(result.newRating);
          console.log(
            `[Rating] Frontend updated to new rating: ${result.newRating} (${change})`
          );

          if (personalisedRatingChangeTimeoutRef.current) {
            clearTimeout(personalisedRatingChangeTimeoutRef.current);
          }
          personalisedRatingChangeTimeoutRef.current = setTimeout(() => {
            SetPersonalisedRatingChange(null);
          }, 3000);
        } else {
          const error = response.data;
          console.error("Failed to update puzzle rating:", error.message);
        }
      } catch (err) {
        console.error("Network error updating puzzle rating:", err);
      }
    };

    updateRatingOnFailure();

    return () => {
      if (personalisedRatingChangeTimeoutRef.current) {
        clearTimeout(personalisedRatingChangeTimeoutRef.current);
      }
    };
  }, [
    currentPuzzle?.blunderDetails.incorrectMove,
    currentPuzzle?.id,
    currentPuzzleIndex,
    username,
    puzzleRating,
    hintsUsedPerMove,
    hasActivatedLiveAnalysis,
    hasInitiatedSelfAnalysis,
    isArchiveMode,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable)
      ) {
        return;
      }
      if (isShowingMoreMovesActiveRef.current) {
        return;
      }
      if (!puzzleStates || puzzleStates.length === 0 || !currentPuzzle) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        if (
          currentPuzzle &&
          currentPuzzle.moveHistory &&
          currentPuzzle.currentMoveIndex >
            (currentPuzzle.puzzleInteractiveStartIndex || 0)
        ) {
          handlePreviousMove();
        }
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        if (
          currentPuzzle &&
          currentPuzzle.moveHistory &&
          currentPuzzle.currentMoveIndex < currentPuzzle.moveHistory.length - 1
        ) {
          handleNextMove();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    console.log(
      "[KeyboardNav Effect] Added keydown listener for puzzle move navigation."
    );

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      console.log(
        "[KeyboardNav Effect] Removed keydown listener for puzzle move navigation."
      );
    };
  }, [
    handlePreviousMove,
    handleNextMove,
    puzzleStates,
    currentPuzzleIndex,
    currentPuzzle,
  ]);

  const handleToggleLiveAnalysis = () => {
    if (personalisedRatingChangeTimeoutRef.current) {
      clearTimeout(personalisedRatingChangeTimeoutRef.current);
    }
    setLastMoveDisplay(null);
    setBestMoveArrow(null);
    SetPersonalisedRatingChange(null);
    setCustomSquareStyles({});
    setShowLiveAnalysisPanel((prev) => {
      const isTurningOn = !prev;
      if (isTurningOn) {
        setHasActivatedLiveAnalysis(true);
      }
      return isTurningOn;
    });
  };
  useEffect(() => {
    const currentFenToAnalyze = currentPuzzle?.currentFen;
    const isPanelActiveAndReady =
      showLiveAnalysisPanel &&
      currentPuzzle &&
      currentFenToAnalyze &&
      isSocketConnected;

    const shouldInitiateNewAnalysisCycle =
      isPanelActiveAndReady &&
      (lastAnalysisFenRef.current !== currentFenToAnalyze ||
        !analysisActiveRef.current);

    if (analysisTimeoutRef.current) {
      clearTimeout(analysisTimeoutRef.current);
      analysisTimeoutRef.current = null;
      console.log(`[SF Personalised] Cleared any pending debounce timeout.`);
    }

    if (analysisActiveRef.current || lastAnalysisFenRef.current) {
      console.log(
        `[SF Personalised] Sending stop command to engine (active: ${
          analysisActiveRef.current
        }, last FEN: ${lastAnalysisFenRef.current ? "set" : "not set"}).`
      );
      stopAnalysis();
      analysisActiveRef.current = false;
      lastAnalysisFenRef.current = "";
    }

    if (shouldInitiateNewAnalysisCycle) {
      console.log(
        `%c[SF Personalised] Scheduling new analysis for FEN: ${currentFenToAnalyze}`,
        "color: LightBlue; font-weight: bold;"
      );

      const debounceTime = 300;
      analysisTimeoutRef.current = setTimeout(() => {
        if (
          showLiveAnalysisPanel &&
          isSocketConnected &&
          currentPuzzle?.currentFen === currentFenToAnalyze
        ) {
          console.log(
            `%c[SF Personalised] Debounce complete. Starting INFINITE analysis for FEN: ${currentFenToAnalyze}`,
            "color: cyan; font-weight: bold;"
          );
          startAnalysis(currentFenToAnalyze, "infinite", 1);
          analysisActiveRef.current = true;
          lastAnalysisFenRef.current = currentFenToAnalyze;
        } else {
          console.log(
            `[SF Personalised] Debounce ended, but conditions changed (e.g., panel hidden, FEN changed again). Not starting analysis.`
          );
        }
        analysisTimeoutRef.current = null;
      }, debounceTime);
    } else if (isPanelActiveAndReady) {
      console.log(
        `[SF Personalised] Analysis already running for FEN: ${currentFenToAnalyze}. No new command needed.`
      );
    } else {
      console.log(
        `[SF Personalised] Live analysis panel is not active or ready. Current state: activePanel=${showLiveAnalysisPanel}, puzzle=${!!currentPuzzle}, fen=${!!currentFenToAnalyze}, socket=${isSocketConnected}.`
      );
    }

    return () => {
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
        analysisTimeoutRef.current = null;
        console.log(
          "[SF Personalised] Effect Cleanup: Cleared pending analysis timeout."
        );
      }
      if (analysisActiveRef.current) {
        stopAnalysis();
        analysisActiveRef.current = false;
        console.log(
          "[SF Personalised] Effect Cleanup: Stopped active analysis."
        );
      }
      lastAnalysisFenRef.current = "";
      console.log("[SF Personalised] Effect Cleanup: Resetting refs.");
    };
  }, [
    showLiveAnalysisPanel,
    currentPuzzle?.currentFen,
    isSocketConnected,
    startAnalysis,
    stopAnalysis,
  ]);
  useEffect(() => {
    const currentPuzzle = puzzleStates[currentPuzzleIndex];

    if (
      currentPuzzle?.awaitingDynamicEngineMove &&
      isSocketConnected &&
      analysisData?.currentFen === currentPuzzle.currentFen &&
      !isLiveAnalyzing &&
      analysisData.bestMove
    ) {
      console.log(
        `%c[Dynamic Engine Move] Final analysis data received for FEN: ${analysisData.currentFen}. Best move: ${analysisData.bestMove}, Depth: ${analysisData.depth}.`,
        "color: magenta; font-weight: bold;"
      );

      const computerMoveSan = formatBestMoveSan(
        analysisData.bestMove,
        currentPuzzle.currentFen
      );

      if (computerMoveSan && computerMoveSan !== "N/A") {
        applyComputerMove(
          computerMoveSan,
          currentPuzzle,
          currentPuzzleIndex,
          analysisData.currentFen,
          setPuzzleStates,
          "dynamic"
        );
      } else {
        console.warn(
          `[Dynamic Engine Move] Analysis completed but no valid best move was found/formatted. Marking puzzle as incorrect.`
        );
        setPuzzleStates((prevStates) => {
          const newStates = [...prevStates];
          const puzzleToUpdate = { ...newStates[currentPuzzleIndex] };
          if (
            puzzleToUpdate.awaitingDynamicEngineMove &&
            puzzleToUpdate.currentFen === currentPuzzle.currentFen
          ) {
            puzzleToUpdate.blunderDetails.incorrectMove = true;
            puzzleToUpdate.isPuzzleInProgress = false;
            puzzleToUpdate.awaitingDynamicEngineMove = false;
            newStates[currentPuzzleIndex] = puzzleToUpdate;
          }
          return newStates;
        });
      }
    } else if (
      currentPuzzle?.awaitingDynamicEngineMove &&
      isLiveAnalyzing &&
      analysisData?.currentFen === currentPuzzle.currentFen &&
      analysisData.bestMove &&
      analysisData.depth >= SCAN_DEPTH
    ) {
      console.log(
        `%c[Dynamic Engine Move] Preemptive stop: Depth ${analysisData.depth} reached with bestMove in INFO. Signaling stop.`,
        "color: orange; font-weight: bold;"
      );
      stopAnalysis();
    } else if (
      currentPuzzle?.awaitingDynamicEngineMove &&
      !isLiveAnalyzing &&
      analysisData?.currentFen === currentPuzzle.currentFen &&
      !analysisData.bestMove
    ) {
      console.warn(
        `[Dynamic Engine Move] Analysis stopped without providing a best move. Marking puzzle as incorrect.`
      );
      setPuzzleStates((prevStates) => {
        const newStates = [...prevStates];
        const puzzleToUpdate = { ...newStates[currentPuzzleIndex] };
        if (
          puzzleToUpdate.awaitingDynamicEngineMove &&
          puzzleToUpdate.currentFen === currentPuzzle.currentFen
        ) {
          puzzleToUpdate.blunderDetails.incorrectMove = true;
          puzzleToUpdate.isPuzzleInProgress = false;
          puzzleToUpdate.awaitingDynamicEngineMove = false;
          newStates[currentPuzzleIndex] = puzzleToUpdate;
        }
        return newStates;
      });
    } else if (
      currentPuzzle?.awaitingDynamicEngineMove &&
      isSocketConnected &&
      (!analysisData || analysisData.currentFen !== currentPuzzle.currentFen) &&
      !isLiveAnalyzing
    ) {
      console.log(
        `[Dynamic Engine Move] Restarting analysis for FEN mismatch or unexpected idle state. Current: ${currentPuzzle.currentFen}, Analysis Data: ${analysisData?.currentFen}`
      );
      startAnalysis(currentPuzzle.currentFen, "depth", SCAN_DEPTH, 1);
    }
  }, [
    analysisData,
    currentPuzzleIndex,
    puzzleStates,
    isSocketConnected,
    isLiveAnalyzing,
    formatBestMoveSan,
    applyComputerMove,
    stopAnalysis,
    startAnalysis,
    SCAN_DEPTH,
  ]);

  useEffect(() => {
    clearTimeout(moveDisplayTimeoutRef.current);

    if (lastMoveDisplay) {
      moveDisplayTimeoutRef.current = setTimeout(() => {
        setLastMoveDisplay(null);
      }, 2000);
    }

    return () => {
      clearTimeout(moveDisplayTimeoutRef.current);
    };
  }, [lastMoveDisplay]);
  const formatLiveEval = (liveEval) => {
    if (!liveEval) return "N/A";

    if (liveEval.type === "cp") {
      return (liveEval.value / 100).toFixed(2);
    }
    if (liveEval.type === "mate") {
      return `M ${liveEval.value}`;
    }
    return "N/A";
  };
  let whiteHeight = 50;
  let blackHeight = 50;

  if (showLiveAnalysisPanel && currentPuzzle?.currentFen) {
    const chess = new Chess(currentPuzzle.currentFen);

    const evaluation =
      analysisData?.evaluation ||
      evalCacheRef.current.get(currentPuzzle.currentFen);
    const boardOrientation =
      currentPuzzle.blunderDetails.userColor === "w" ? "white" : "black";

    if (chess.isCheckmate()) {
      const MatedPlayer = chess.turn();

      if (MatedPlayer === "w") {
        whiteHeight = 0;
        blackHeight = 100;
      } else {
        whiteHeight = 100;
        blackHeight = 0;
      }
    } else if (evaluation) {
      if (evaluation.type === "mate") {
        const mateValue = evaluation.value;
        if (mateValue === 0) {
          whiteHeight = 50;
          blackHeight = 50;
        } else if (mateValue > 0) {
          whiteHeight = 100;
          blackHeight = 0;
        } else {
          whiteHeight = 0;
          blackHeight = 100;
        }
      } else if (evaluation.type === "cp") {
        const maxCpDisplay = 600;
        const cpValue = evaluation.value;
        const clampedCp = Math.max(
          -maxCpDisplay,
          Math.min(maxCpDisplay, cpValue)
        );
        const whiteAdvantageRatio =
          (clampedCp + maxCpDisplay) / (2 * maxCpDisplay);
        whiteHeight = whiteAdvantageRatio * 100;
        blackHeight = 100 - whiteHeight;
      }
    }
  }
  async function onSquareClick(square) {
    if (isShowingMoreMovesActive) return false;
    if (currentPuzzle.awaitingDynamicEngineMove) return;

    const game = new Chess(currentPuzzle.currentFen);

    if (moveFrom) {
      const moves = game.moves({ square: moveFrom, verbose: true });
      const foundMove = moves.find((m) => m.to === square);

      if (foundMove) {
        if (foundMove.flags.includes("p")) {
          setPromotionMove({ from: moveFrom, to: square });
          setPromotionSquare(square);
          setPromotionDialogOpen(true);
        } else {
          await handlePieceDrop(moveFrom, square);
        }
        cleanupMoveState();
        return;
      }
    }

    const isPuzzleOver =
      currentPuzzle.blunderDetails.puzzleSolved ||
      currentPuzzle.blunderDetails.incorrectMove ||
      puzzleHasBeenFailedOnce ||
      currentPuzzle.continuePlayFailure;
    const pieceOnSquare = game.get(square);

    if (
      !isPuzzleOver &&
      (!pieceOnSquare || pieceOnSquare.color !== game.turn())
    ) {
      cleanupMoveState();
      return;
    }
    if (!pieceOnSquare) {
      cleanupMoveState();
      return;
    }
    if (moveFrom === square) {
      cleanupMoveState();
      return;
    }
    highlightLegalMoves(square, game);
  }
  function highlightLegalMoves(square, gameInstance) {
    const moves = gameInstance.moves({ square, verbose: true });
    const newOptionSquares = {};
    moves.forEach((move) => {
      if (move.flags.includes("c")) {
        newOptionSquares[move.to] = {
          boxShadow: "inset 0 0 0 5.5px rgba(0, 0, 0, 0.2)",
          borderRadius: "50%",
        };
      } else {
        newOptionSquares[move.to] = {
          background:
            "radial-gradient(circle, rgba(0,0,0,0.2) 25%, transparent 25%)",
          borderRadius: "50%",
        };
      }
    });
    setMoveFrom(square);
    setOptionSquares(newOptionSquares);
  }

  let isFinalPositionCheckmate = false;
  if (currentPuzzle && currentPuzzle.blunderDetails.puzzleSolved) {
    const finalGame = new Chess(currentPuzzle.currentFen);
    isFinalPositionCheckmate = finalGame.isCheckmate();
  }

  const defaultOrientation =
    currentPuzzle?.blunderDetails?.userColor === "w" ? "white" : "black";
  const isBoardFlipped = boardOrientation !== defaultOrientation;

  useEffect(() => {
    const triggerReconnection = () => {
      if (!isSocketConnected) {
        console.log(
          "[Auto-Reconnect] Connection lost. Attempting to reconnect..."
        );
        stockfishSocketService.connect();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerReconnection();
      }
    };

    const handleOnline = () => {
      triggerReconnection();
    };

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        triggerReconnection();
      }
    }, 3000);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      clearInterval(intervalId);
    };
  }, [isSocketConnected]);
  return (
    <div
      className="container responsive-page-shell personalised-page-root"
      style={{ userSelect: "text", padding: "clamp(6px, 2vw, 20px)" }}
    >
      {statusMessage}
      <div className="mobile-landscape-blocker" role="alert" aria-live="polite">
        <h3>Rotate your device</h3>
        <p>Personalized Puzzles is available in portrait mode only.</p>
      </div>
      {!loadingGames &&
        !loadingPuzzles &&
        !loadingProcessedGames &&
        puzzleStates.length > 0 &&
        currentPuzzle && (
          <div className="blunders-container personalised-blunders-container">
            <div
              className="blunder-item"
              style={{
                marginBottom: "30px",
                paddingBottom: "20px",
                display: "flex",
                gap: "20px",
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              <div className="mobile-puzzle-top-title">
                <h3>
                  Your Personalized Puzzles ({currentPuzzleIndex + 1} of{" "}
                  {puzzleStates.length})
                  {isGeneratingPuzzles && (
                    <span
                      style={{
                        fontSize: "0.6em",
                        marginLeft: "10px",
                        color: "#888",
                      }}
                    >
                      (Searching...)
                    </span>
                  )}
                </h3>
              </div>

              <div className="mobile-personalised-summary">
                <p>
                  <strong>Game:</strong> vs {currentPuzzle.blunderDetails.opponent}{" "}
                  {currentPuzzle.blunderDetails.gameSource === "manual-import" ? (
                    <span style={{ color: "#666", fontStyle: "italic" }}>
                      (Imported from PGN)
                    </span>
                  ) : (
                    <span>
                      (
                      <a
                        href={currentPuzzle.blunderDetails.gameUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {currentPuzzle.blunderDetails.gameSource === "lichess"
                          ? "View on Lichess.org"
                          : "View on Chess.com"}
                      </a>
                      )
                    </span>
                  )}
                </p>
                <div
                  className={`mobile-rating-chip ${
                    loadingRating ? "is-loading" : ""
                  }`}
                >
                  <span className="mobile-rating-label">Rating</span>
                  <strong className="mobile-rating-value">
                    {loadingRating ? "..." : puzzleRating ?? "--"}
                  </strong>
                  {personalisedRatingChange !== null &&
                    personalisedRatingChange !== 0 && (
                      <span
                        className={`mobile-rating-change ${
                          personalisedRatingChange > 0 ? "positive" : "negative"
                        }`}
                      >
                        {personalisedRatingChange > 0 ? "+" : ""}
                        {personalisedRatingChange}
                      </span>
                    )}
                </div>
                <p
                  className={`mobile-post-attempt-line ${
                    currentPuzzle.blunderDetails.puzzleSolved ||
                    currentPuzzle.blunderDetails.incorrectMove ||
                    currentPuzzle.continuePlayFailure
                      ? "visible"
                      : "hidden"
                  }`}
                >
                  {currentPuzzle.blunderDetails.puzzleSolved ||
                  currentPuzzle.blunderDetails.incorrectMove ||
                  currentPuzzle.continuePlayFailure ? (
                    <>
                      Your move{" "}
                      <strong>{currentPuzzle.blunderDetails.blunderMoveSan}</strong>{" "}
                      was a blunder.
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </p>
                <p
                  className={`mobile-post-attempt-line ${
                    currentPuzzle.blunderDetails.puzzleSolved ||
                    currentPuzzle.blunderDetails.incorrectMove ||
                    currentPuzzle.continuePlayFailure
                      ? "visible"
                      : "hidden"
                  }`}
                >
                  {currentPuzzle.blunderDetails.puzzleSolved ||
                  currentPuzzle.blunderDetails.incorrectMove ||
                  currentPuzzle.continuePlayFailure ? (
                    currentPuzzle.blunderDetails.incorrectMove &&
                    currentPuzzle.isRepeatedBlunder ? (
                      <>⚠️ You made the same blunder as in the game!</>
                    ) : (
                      <>
                        Evaluation changed from{" "}
                        {currentPuzzle.blunderDetails.prevEvalCp / 100} to{" "}
                        {currentPuzzle.blunderDetails.currentEvalCp / 100} (Drop:{" "}
                        {currentPuzzle.blunderDetails.evalDropCp / 100})
                      </>
                    )
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </p>
              </div>

              <div
                className="personalised-board-row"
                style={{ display: "flex", alignItems: "stretch" }}
              >
                {showLiveAnalysisPanel && (
                  <div
                    className="personalised-eval-bar"
                    style={{
                      backgroundColor: "#333",
                      borderRadius: "4px",
                      overflow: "hidden",
                      position: "relative",
                      flexShrink: 0,
                      transform:
                        currentPuzzle.blunderDetails.userColor === "b"
                          ? "rotate(180deg)"
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: `${blackHeight}%`,
                        backgroundColor: "#333",
                        transition: "height 0.3s ease",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: `${blackHeight}%`,
                        left: 0,
                        right: 0,
                        height: `${whiteHeight}%`,
                        backgroundColor: "#fff",
                        transition: "height 0.3s ease, top 0.3s ease",
                      }}
                    />
                  </div>
                )}

                <div
                  className="puzzle-board-personalised"
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  <Chessboard
                    id={`puzzle-board-${currentPuzzle.id}`}
                    position={currentPuzzle.currentFen}
                    onPieceDrop={handlePieceDrop}
                    onSquareClick={onSquareClick}
                    customArrows={
                      bestMoveArrow
                        ? [
                            [
                              bestMoveArrow.from,
                              bestMoveArrow.to,
                              "rgb(6, 127, 214)",
                            ],
                          ]
                        : []
                    }
                    boardOrientation={boardOrientation}
                    customLightSquareStyle={customLightSquareStyle}
                    customDarkSquareStyle={customDarkSquareStyle}
                    animationDuration={200}
                    customSquareStyles={{
                      ...customSquareStyles,
                      ...optionSquares,
                      ...(moveFrom && {
                        [moveFrom]: {
                          background: "rgba(0, 115, 255, 0.3)",
                        },
                      }),
                      ...(currentMoveHintLevel >= 1 &&
                        hintMove && {
                          [hintMove.from]: {
                            background: "rgba(225, 100, 244, 0.5)",
                          },
                        }),
                      ...(currentMoveHintLevel >= 2 &&
                        hintMove && {
                          [hintMove.to]: {
                            background: "rgba(225, 100, 244, 0.4)",
                          },
                        }),
                    }}
                    showPromotionDialog={promotionDialogOpen}
                    promotionToSquare={promotionSquare}
                    promotionDialogVariant="vertical"
                    onPromotionPieceSelect={(piece) => {
                      if (!piece) return false;
                      const promotionChoice = piece[1].toLowerCase();

                      if (promotionMove) {
                        handlePieceDrop(
                          promotionMove.from,
                          promotionMove.to,
                          promotionChoice
                        );
                      }

                      setPromotionDialogOpen(false);
                      setPromotionSquare(null);
                      setPromotionMove(null);
                      return true;
                    }}
                    onDialogClose={() => {
                      setPromotionDialogOpen(false);
                      setPromotionSquare(null);
                      setPromotionMove(null);
                    }}
                  />
                  {(currentPuzzle.blunderDetails.puzzleSolved ||
                    currentPuzzle.blunderDetails.incorrectMove ||
                    puzzleHasBeenSolvedOnce ||
                    puzzleHasBeenFailedOnce ||
                    currentPuzzle.continuePlayFailure ||
                    hasActivatedLiveAnalysis ||
                    hasInitiatedSelfAnalysis) &&
                    !isContinuePlayingActive &&
                    !isShowingBlunderAnalysis &&
                    !isShowingMoreMovesActive && (
                      <div
                        className="flip-icon-wrapper-personalised-puzzles"
                        onClick={handleBoardFlip}
                      >
                        <FlipIcon className="flip-icon-personalised-puzzles" />
                      </div>
                    )}
                </div>
              </div>

              <div className="personalised-side-panel" style={{ flexGrow: 1 }}>
                <h3 className="personalised-primary-text">
                  Your Personalized Puzzles ({currentPuzzleIndex + 1} of{" "}
                  {puzzleStates.length})
                  {isGeneratingPuzzles && (
                    <span
                      style={{
                        fontSize: "0.6em",
                        marginLeft: "10px",
                        color: "#888",
                      }}
                    >
                      (Searching...)
                    </span>
                  )}
                </h3>
                {currentPuzzle.blunderDetails.puzzleSolved &&
                  !hasActivatedLiveAnalysis &&
                  !hasInitiatedSelfAnalysis && (
                    <p
                      className="default-solved-message"
                      style={{ color: "green", fontWeight: "bold" }}
                    >
                      🎉 Puzzle Solved! Well done! 🎉
                    </p>
                  )}
                {currentPuzzle.blunderDetails.incorrectMove &&
                  !hasActivatedLiveAnalysis &&
                  !hasInitiatedSelfAnalysis && (
                    <p
                      className="default-incorrect-message"
                      style={{ color: "red", fontWeight: "bold" }}
                    >
                      {currentPuzzle.isRepeatedBlunder ? (
                        <>
                          ⚠️ You made the same blunder as in the game! <br />
                        </>
                      ) : (
                        "❌ Incorrect Move! Reset the puzzle to try again. ❌"
                      )}
                    </p>
                  )}
                <p className="personalised-primary-text">
                  <strong>Game:</strong> vs{" "}
                  {currentPuzzle.blunderDetails.opponent}{" "}
                  {currentPuzzle.blunderDetails.gameSource ===
                  "manual-import" ? (
                    <span style={{ color: "#666", fontStyle: "italic" }}>
                      (Imported from PGN)
                    </span>
                  ) : (
                    <span>
                      (
                      <a
                        href={currentPuzzle.blunderDetails.gameUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {currentPuzzle.blunderDetails.gameSource === "lichess"
                          ? "View on Lichess.org"
                          : "View on Chess.com"}
                      </a>
                      )
                    </span>
                  )}
                  {currentPuzzle.blunderDetails.source === "database" && (
                    <span
                      style={{
                        fontSize: "0.8em",
                        color: "gray",
                        marginLeft: "10px",
                      }}
                    >
                      (Loaded from DB)
                    </span>
                  )}
                  {currentPuzzle.blunderDetails.source === "analysis" && (
                    <span
                      style={{
                        fontSize: "0.8em",
                        color: "green",
                        marginLeft: "10px",
                      }}
                    >
                      (Newly Found)
                    </span>
                  )}
                </p>
                {currentPuzzle &&
                  currentPuzzle.blunderDetails
                    .opponentLastMoveSanBeforeBlunder && (
                    <p className="personalised-primary-text">
                      Opponent's move before your blunder:{" "}
                      <strong>
                        {
                          currentPuzzle.blunderDetails
                            .opponentLastMoveSanBeforeBlunder
                        }
                      </strong>
                    </p>
                  )}
                {loadingRating ? (
                  <p className="personalised-primary-text">
                    Loading puzzle rating...
                  </p>
                ) : puzzleRating !== null ? (
                  <p className="personalised-primary-text">
                    Your Current Personalized Puzzle Rating:{" "}
                    <strong>
                      {puzzleRating}
                      {personalisedRatingChange !== null &&
                        personalisedRatingChange !== 0 && (
                          <span
                            style={{
                              color:
                                personalisedRatingChange > 0 ? "green" : "red",
                              marginLeft: "8px",
                              fontSize: "0.9em",
                            }}
                          >
                            ({personalisedRatingChange > 0 ? "+" : ""}
                            {personalisedRatingChange})
                          </span>
                        )}
                    </strong>
                  </p>
                ) : (
                  <p className="personalised-primary-text">...</p>
                )}
                {currentPuzzle.blunderDetails.puzzleSolved &&
                  !hasActivatedLiveAnalysis &&
                  !hasInitiatedSelfAnalysis && (
                    <div className="mobile-solved-actions-row">
                      <p
                        style={{
                          color: "green",
                          fontWeight: "bold",
                          margin: 0,
                        }}
                      >
                        🎉 Puzzle Solved! Well done! 🎉
                      </p>
                      {currentPuzzle.blunderDetails.puzzleSolved &&
                        !hasBeenReset &&
                        !hasInitiatedContinuePlaying &&
                        !isContinuePlayingActive &&
                        !isFinalPositionCheckmate &&
                        !hasActivatedLiveAnalysis &&
                        !hasInitiatedSelfAnalysis && (
                          <button
                            onClick={handleContinuePlaying}
                            disabled={isBoardFlipped}
                            style={{
                              padding: "8px 10px",
                              fontSize: "0.85em",
                              userSelect: "none",
                              cursor: isBoardFlipped
                                ? "not-allowed"
                                : "pointer",
                              backgroundColor: isBoardFlipped ? "#ccc" : "#28a745",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                            }}
                          >
                            Continue Puzzle
                          </button>
                        )}
                    </div>
                  )}
                <p className="default-move-blunder-wrapper">
                  {(currentPuzzle.blunderDetails.puzzleSolved ||
                    currentPuzzle.blunderDetails.incorrectMove ||
                    currentPuzzle.continuePlayFailure) && (
                    <span className="default-move-blunder-text">
                      Your move{" "}
                      <strong>
                        {currentPuzzle.blunderDetails.blunderMoveSan}
                      </strong>{" "}
                      was a blunder.
                    </span>
                  )}
                  {currentPuzzle.blunderDetails.puzzleSolved &&
                    !hasBeenReset &&
                    !hasInitiatedContinuePlaying &&
                    !isContinuePlayingActive &&
                    !isFinalPositionCheckmate &&
                    !hasActivatedLiveAnalysis &&
                    !hasInitiatedSelfAnalysis && (
                      <button
                        className="continue-puzzle-inline"
                        onClick={handleContinuePlaying}
                        disabled={isBoardFlipped}
                        style={{
                          marginTop: "10px",
                          marginLeft: "30px",
                          padding: "10px 10px",
                          fontSize: "0.9em",
                          cursor: "pointer",
                          userSelect: "none",
                          cursor: isBoardFlipped ? "not-allowed" : "pointer",
                          backgroundColor: isBoardFlipped ? "#ccc" : "#28a745",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                        }}
                      >
                        Continue Puzzle
                      </button>
                    )}
                </p>

                {currentPuzzle.blunderDetails.incorrectMove &&
                  !hasBeenReset &&
                  !hasShownCounter &&
                  !hasActivatedLiveAnalysis &&
                  !hasInitiatedSelfAnalysis &&
                  (!isShowingMoreMovesActive || !isShowingBlunderAnalysis) && (
                    <div className="mobile-inline-analysis-row">
                      {!isShowingMoreMovesActive && (
                        <button
                          className="mobile-inline-analysis-button"
                          onClick={handleShowMoreMoves}
                          disabled={isBoardFlipped}
                          style={{
                            marginTop: "10px",
                            marginLeft: "30px",
                            padding: "10px 10px",
                            fontSize: "0.9em",
                            cursor: "pointer",
                            userSelect: "none",
                            cursor: isBoardFlipped ? "not-allowed" : "pointer",
                            backgroundColor: isBoardFlipped ? "#ccc" : "#1E90FF",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        >
                          show best moves
                        </button>
                      )}
                      {!isShowingBlunderAnalysis && (
                        <button
                          className="mobile-inline-analysis-button"
                          onClick={handleShowBlunderAnalysis}
                          disabled={isShowingBlunderAnalysis || isBoardFlipped}
                          style={{
                            marginTop: "10px",
                            marginLeft: "10px",
                            padding: "10px 10px",
                            fontSize: "0.9em",
                            cursor: "pointer",
                            cursor:
                              isShowingBlunderAnalysis || isBoardFlipped
                                ? "not-allowed"
                                : "pointer",
                            backgroundColor:
                              isShowingBlunderAnalysis || isBoardFlipped
                                ? "#ccc"
                                : "#FFA500",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        >
                          {isShowingBlunderAnalysis
                            ? "Analyzing..."
                            : "show counter"}{" "}
                        </button>
                      )}
                    </div>
                  )}
                <p className="default-eval-change-wrapper">
                  {(currentPuzzle.blunderDetails.puzzleSolved ||
                    currentPuzzle.blunderDetails.incorrectMove ||
                    currentPuzzle.continuePlayFailure) && (
                    <span className="default-eval-change-text">
                      Evaluation changed from{" "}
                      {currentPuzzle.blunderDetails.prevEvalCp / 100} to{" "}
                      {currentPuzzle.blunderDetails.currentEvalCp / 100} (Drop:{" "}
                      {currentPuzzle.blunderDetails.evalDropCp / 100})
                    </span>
                  )}
                </p>
                {currentPuzzle.blunderDetails.analysisDepth && (
                  <p>
                    Analysis Depth:{" "}
                    <strong>
                      {currentPuzzle.blunderDetails.analysisDepth}
                    </strong>
                  </p>
                )}
                {lastMoveDisplay && (
                  <div
                    className="personalised-last-move-banner"
                    style={{
                      margin: "30px auto",
                      padding: "13px 10px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor:
                        lastMoveDisplay.status === "correct"
                          ? "green"
                          : lastMoveDisplay.status === "incorrect"
                          ? "red"
                          : "#000",
                      textAlign: "center",
                      borderRadius: "8px",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <span style={{ fontSize: "1.1em", fontWeight: "500" }}>
                      {lastMoveDisplay.text}
                    </span>
                  </div>
                )}
                <div className="puzzle-actions-row" style={{ marginTop: "10px" }}>
                  <button
                    onClick={handlePreviousMove}
                    disabled={
                      !currentPuzzle ||
                      !currentPuzzle.moveHistory ||
                      currentPuzzle.currentMoveIndex <=
                        currentPuzzle.puzzleInteractiveStartIndex ||
                      isShowingMoreMovesActive
                    }
                    style={{
                      marginRight: "5px",
                      padding: "5px 10px",
                      fontSize: "0.9em",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    title="Go to previous move"
                  >
                    ⬅️ Previous
                  </button>
                  <button
                    onClick={handleNextMove}
                    disabled={
                      !currentPuzzle ||
                      !currentPuzzle.moveHistory ||
                      currentPuzzle.currentMoveIndex >=
                        currentPuzzle.moveHistory.length - 1 ||
                      isShowingMoreMovesActive
                    }
                    style={{
                      marginRight: "10px",
                      padding: "5px 10px",
                      fontSize: "0.9em",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    title="Go to next move"
                  >
                    Next ➡️
                  </button>
                  {!currentPuzzle.blunderDetails.puzzleSolved &&
                    !puzzleHasBeenFailedOnce &&
                    !hasInitiatedContinuePlaying &&
                    !isContinuePlayingActive &&
                    !isShowingMoreMovesActive && (
                      <button
                        onClick={handleHintClick}
                        disabled={isHintLoading}
                        style={{
                          marginRight: "10px",
                          padding: "6px 10px",
                          fontSize: "0.9em",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        title="Show a hint for the best move"
                      >
                        {isHintLoading ? "Thinking..." : "Hint"}
                      </button>
                    )}
                  {(puzzleHasBeenSolvedOnce || puzzleHasBeenFailedOnce) && (
                    <button
                      onClick={handleResetBoard}
                      disabled={
                        isShowingMoreMovesActive || isContinuePlayingActive
                      }
                      style={{
                        marginTop: "10px",
                        marginRight: "10px",
                        padding: "5px 10px",
                        fontSize: "0.9em",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      Reset
                    </button>
                  )}
                  {(puzzleHasBeenSolvedOnce || puzzleHasBeenFailedOnce) && (
                    <button
                      onClick={handleToggleLiveAnalysis}
                      disabled={
                        (!isSocketConnected && !showLiveAnalysisPanel) ||
                        isShowingMoreMovesActive ||
                        isContinuePlayingActive
                      }
                      style={{
                        marginTop: "10px",
                        marginRight: "10px",
                        padding: "5px 10px",
                        fontSize: "0.9em",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {showLiveAnalysisPanel
                        ? isLiveAnalyzing
                          ? `Analyzing`
                          : isSocketConnected
                          ? "Hide Analysis"
                          : "Connecting..."
                        : isSocketConnected
                        ? "Live Analysis"
                        : "Engine Connecting..."}
                    </button>
                  )}
                  <div className="archive-mode-wrapper">
                    {isArchiveMode && (
                      <div
                        className="archive-mode-inline"
                        style={{ marginBottom: "15px" }}
                      >
                        <button
                          onClick={handleBackToQueue}
                          style={{
                            padding: "8px 12px",
                            backgroundColor: "#6c757d", // Grey color to differentiate from main actions
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.9em",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          ⬅ Back to Active Queue
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {showLiveAnalysisPanel && isSocketConnected && (
                  <div
                    className="live-analysis-panel personalised-live-panel"
                    style={{
                      marginTop: "15px",
                      padding: "10px",
                      border: "1px solid #ddd",
                      backgroundColor: "#000",
                      color: "#fff",
                      fontSize: "0.9em",
                      borderRadius: "4px",
                      boxSizing: "border-box",
                    }}
                  >
                    <h4>
                      Engine Analysis (
                      {currentPuzzle && currentPuzzle.currentFen
                        ? new Chess(currentPuzzle.currentFen).turn() === "w"
                          ? "White's"
                          : "Black's"
                        : ""}{" "}
                      turn)
                    </h4>
                    {isLiveAnalyzing &&
                      !analysisData?.evaluation &&
                      !analysisData?.bestMove && (
                        <p>Status: Starting analysis...</p>
                      )}
                    {isLiveAnalyzing &&
                      (analysisData?.evaluation || analysisData?.bestMove) && (
                        <p>
                          Status: Thinking (Depth: {analysisData?.depth || 0}
                          )...
                        </p>
                      )}
                    {!isLiveAnalyzing && analysisData?.bestMove && (
                      <p>Status: Analysis complete.</p>
                    )}
                    {!isLiveAnalyzing && !analysisData?.bestMove && (
                      <p>
                        Status: Idle. Click "Live Analysis" to start if panel is
                        open.
                      </p>
                    )}
                    <p>
                      Evaluation:{" "}
                      <strong>{formatLiveEval(analysisData.evaluation)}</strong>
                    </p>
                    <p>
                      Best Move:{" "}
                      <strong>
                        {formatBestMoveSan(
                          analysisData.bestMove,
                          currentPuzzle?.currentFen
                        )}
                      </strong>
                    </p>
                    <p
                      style={{
                        wordBreak: "break-all",
                        overflowWrap: "break-word",
                      }}
                    >
                      Principal Variation:{" "}
                      {formatPvSan(
                        analysisData.pv,
                        currentPuzzle?.currentFen
                      ) || "N/A"}
                    </p>
                  </div>
                )}
                {showLiveAnalysisPanel && !isSocketConnected && (
                  <div
                    style={{
                      marginTop: "15px",
                      padding: "10px",
                      border: "1px solid #ddd",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <p>Loading...</p>
                  </div>
                )}
                {(hasActivatedLiveAnalysis || hasInitiatedSelfAnalysis) &&
                  (currentPuzzle.fullMoveSANs?.length > 0 ||
                    currentPuzzle.analysisMoveTree) && (
                    <div
                      className="analysis-moves-display personalised-analysis-moves"
                      style={{
                        marginTop: "20px",
                        padding: "10px",
                        border: "1px solid #555",
                        borderRadius: "4px",
                        backgroundColor: "#333",
                        color: "#fff",
                        fontFamily: "monospace",
                        lineHeight: "1.6",
                        wordBreak: "break-word",
                      }}
                    >
                      {formatPuzzleMovesForAnalysis(currentPuzzle)}
                      {formatAnalysisMoves(currentPuzzle)}
                    </div>
                  )}
                <div
                  className="puzzle-navigation puzzle-navigation-controls"
                  style={{ marginTop: "20px", textAlign: "center" }}
                >
                  {(puzzleHasBeenSolvedOnce || puzzleHasBeenFailedOnce) && (
                    <button
                      onClick={handlePreviousPuzzle}
                      disabled={
                        currentPuzzleIndex === 0 || isShowingMoreMovesActive
                      }
                      style={{
                        padding: "8px 15px",
                        marginRight: "15px",
                        fontSize: "1em",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      Previous Puzzle
                    </button>
                  )}
                  {(puzzleHasBeenSolvedOnce || puzzleHasBeenFailedOnce) && (
                    <button
                      onClick={handleNextPuzzle}
                      disabled={
                        puzzleStates.length === 0 ||
                        currentPuzzleIndex === puzzleStates.length - 1 ||
                        isShowingMoreMovesActive
                      }
                      style={{
                        padding: "8px 15px",
                        fontSize: "1em",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      Next Puzzle
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      {isImportModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="personalised-import-modal"
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            <h3 style={{ margin: 0, color: "#333" }}>Import Game PGN</h3>
            <p style={{ margin: 0, fontSize: "0.9em", color: "#666" }}>
              Paste the PGN of a game below to generate puzzles from it.
            </p>
            <textarea
              value={pgnInputText}
              onChange={(e) => {
                setPgnInputText(e.target.value);
                if (importError) setImportError(null);
              }}
              placeholder='Paste PGN here (e.g., "[Event \"Casual Game\"] 1. e4 e5 2. Nf3 Nc6 ...")'
              rows={10}
              style={{
                width: "100%",
                padding: "10px",
                boxSizing: "border-box",
                borderRadius: "4px",
                border: importError ? "1px solid red" : "1px solid #ccc",
                fontFamily: "monospace",
                resize: "vertical",
              }}
            />
            {importError && (
              <p style={{ color: "red", fontSize: "0.9em", margin: "0" }}>
                ⚠️ {importError}
              </p>
            )}
            <div
              className="personalised-import-modal-actions"
            >
              <button
                onClick={handleCloseImportModal}
                style={{
                  padding: "8px 15px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePgnSubmit}
                style={{
                  padding: "8px 15px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Analyze Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
